import { useEffect, useMemo, useRef, useState } from 'react'
import {
  FileStack,
  ReceiptText,
  FileText,
  ShieldCheck,
  Plus,
  Loader2,
  X,
  Download,
  Trash2,
  AlertTriangle,
  ExternalLink,
  Check,
  Minus,
  Maximize2,
  Minimize2,
  ChevronDown,
  Search,
  LayoutGrid,
} from 'lucide-react'
import {
  IconReceipt, IconReportMoney, IconCertificate, IconFileInvoice,
  IconBuildingHospital, IconPhotoScan, IconDental,
} from '@tabler/icons-react'
import { apiGet, redirectToLogin } from '../lib/api.js'
import { formatThaiDate, asList, getDoctorImage } from '../lib/chart-helpers.js'
import { useMe } from '../lib/me.js'
import { isEmployee } from '../lib/role.js'
import { isTraining, TRAINING_DOC_OFF_TH, TRAINING_FILES_OFF_DETAIL_TH } from '../lib/training.js'
import { useToast } from '../kit'
import { bkkDay, fetchAllTreatments } from '../lib/billing.js'
import { useTypeahead } from '../lib/useTypeahead.js'
import ConfirmDialog from './ConfirmDialog.jsx'
import { isHtmlTemplate, renderDocumentHtml, documentAssets } from '../lib/documents/index.js'
import RichTextEditor from './RichTextEditor.jsx'

/**
 * Generate treatment-tied PDF documents (receipt, medical certificate, …) via
 * the smileseasons_app Apps Script renderer, then attach the resulting PDF to
 * this visit's chart.document[] (so it also shows up in the chart File browser).
 *
 * Flow:
 *   POST /api/documents/generate { template, data }
 *     → Apps Script fills a Google Docs template, exports a PDF to Drive,
 *       returns { success, fileId, fileName, url, downloadUrl }.
 *   PATCH /api/charts/:id { document: [...existing, newEntry] }
 *     → attaches the Drive PDF as a generated document on the chart.
 *
 * Each doc type declares its own editable fields; patient/treatment context is
 * always sent as base data so templates can fill {{patient_name}}, {{hn}}, etc.
 */

const DOC_DEFS = [
  {
    key: 'receipt', label: 'ใบเสร็จ', icon: IconReceipt, doc_type: 'receipt',
    fields: [
      { name: 'items',          label: 'รายการ',        type: 'textarea', placeholder: 'เช่น ขูดหินปูน, อุดฟัน' },
      { name: 'amount',         label: 'จำนวนเงิน (บาท)', type: 'number' },
      { name: 'payment_method', label: 'วิธีชำระเงิน',    type: 'text', default: 'เงินสด' },
    ],
  },
  // Treatment-fee detail statement — billing-driven like the receipt, but NOT
  // grouped (one row per billing line at priceXunit) and no next-appointment.
  { key: 'fee_detail', label: 'เอกสารแสดงรายละเอียดค่ารักษาพยาบาล', icon: IconReportMoney, doc_type: 'fee_detail', fields: [] },
  {
    key: 'medical_cert', label: 'ใบรับรองแพทย์', icon: IconCertificate, doc_type: 'medical_cert',
    fields: [
      { name: 'diagnosis',      label: 'การวินิจฉัย',     type: 'textarea' },
      { name: 'rest_days',      label: 'จำนวนวันพัก',     type: 'number' },
      { name: 'doctor_license', label: 'เลขที่ใบประกอบฯ', type: 'text' },
    ],
  },
  // ใบแจ้งหนี้ — identical billing logic to the receipt; only the template wording differs.
  { key: 'invoice', label: 'ใบแจ้งหนี้', icon: IconFileInvoice, doc_type: 'invoice', fields: [] },
  // ใบส่งตัว — same pattern as the medical certificate; one rich-text field (refer_detail).
  { key: 'referral', label: 'ใบส่งตัว', icon: IconBuildingHospital, doc_type: 'referral', fields: [] },
  // ใบส่งเอ็กซเรย์ — Thai-only; one rich-text field (refer_detail), same pattern as ใบส่งตัว.
  { key: 'xray_referral', label: 'ใบส่งเอ็กซเรย์', icon: IconPhotoScan, doc_type: 'xray_referral', fields: [] },
  // ตรวจสุขภาพ Tooth Taxi — Thai-only printable form; no input, all fields DB-derived.
  { key: 'toothtaxi_checkup', label: 'ตรวจสุขภาพ Tooth taxi', icon: IconDental, doc_type: 'toothtaxi_checkup', fields: [] },
]

const DEF_BY_TYPE = Object.fromEntries(DOC_DEFS.map((d) => [d.doc_type, d]))

// Billing/financial documents — hidden from non-employee (doctor-only) sessions
// so the chart can't be used to print receipts/invoices (which expose pricing).
// Exported so the File section can apply the same role gate.
export const BILLING_DOC_KEYS = new Set(['receipt', 'fee_detail', 'invoice'])

// A "generated document" is one created here (flagged) or whose doc_type maps to
// a known template. These are surfaced in this Documents section, so the File
// section excludes them to avoid showing the same PDF twice.
export function isGeneratedDoc(doc) {
  return !!(doc?.generated || DEF_BY_TYPE[doc?.doc_type])
}

// The Thai label of a stored document (ใบเสร็จ, ใบรับรองแพทย์…), for lists outside
// this section that only have the chart.document[] entry.
export function docLabel(doc) {
  return DEF_BY_TYPE[doc?.doc_type]?.label || doc?.name || 'เอกสาร'
}

// Billing doc? (receipt / fee_detail / invoice — the price-exposing ones.)
export function isBillingDoc(doc) {
  return BILLING_DOC_KEYS.has(doc?.doc_type)
}

// HTML templates (src/lib/documents/) render in-house instead of through Apps
// Script — but only once the server says its HTML → PDF backend is configured
// (GET /api/documents/render). Asked once per page load; any failure means "no",
// which keeps the old renderer working rather than leaving staff with nothing.
let htmlRendererProbe = null
function probeHtmlRenderer() {
  if (!htmlRendererProbe) {
    htmlRendererProbe = fetch('/api/documents/render', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => !!j?.available)
      .catch(() => false)
  }
  return htmlRendererProbe
}

// Same-origin URLs for the browser preview; the server inlines the same files.
const browserDocAssets = () => documentAssets((path) => `${window.location.origin}/doc/${path}`)

// The most-used docs get prominent buttons; the rest collapse into a searchable
// "เอกสารอื่นๆ" dropdown to keep the create row uncluttered.
const PRIMARY_DOC_KEYS = ['receipt', 'invoice', 'medical_cert']

// The treatment card's menu promotes a shorter list to its first level — ใบแจ้งหนี้
// is rare at the counter, ใบเสร็จ and ใบรับรองแพทย์ are not (Ram, 2026-10-15).
export const QUICK_DOC_KEYS = ['receipt', 'medical_cert']

/**
 * The whole "issue a document" flow — dialog → PDF preview → Wasabi → attach to
 * chart.document[] — without the Documents section around it. The chart's
 * Documents section uses it, and so does the treatment card's ออกเอกสาร menu
 * (PatientTreatments), so a document issued from either place is the same file
 * made the same way. Render `dialogs` somewhere that stays mounted.
 */
export function useDocumentIssuer({ treatment, chart, patient, hn, onSaved }) {
  const chartId = chart?.id
  const docs = Array.isArray(chart?.document) ? chart.document : []

  const [activeDef, setActiveDef] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [preview, setPreview] = useState(null)   // { out } | { html, template, payload } — awaiting accept/reject
  const [htmlReady, setHtmlReady] = useState(false)
  // Sandbox refusals go to a toast, not `err`: both are raised from inside a modal
  // (the form, the preview), and `err` renders underneath it.
  const toast = useToast()
  useEffect(() => {
    let alive = true
    probeHtmlRenderer().then((ok) => { if (alive) setHtmlReady(ok) })
    return () => { alive = false }
  }, [])

  const me = useMe()
  const showBilling = isEmployee(me)
  // Doctor-only sessions don't get the billing docs (ใบเสร็จ / รายละเอียดค่ารักษา / ใบแจ้งหนี้).
  const visibleDefs = useMemo(
    () => DOC_DEFS.filter((def) => showBilling || !BILLING_DOC_KEYS.has(def.key)),
    [showBilling],
  )

  const baseData = useMemo(() => buildBaseData(patient, hn, treatment), [patient, hn, treatment])

  function open(def) {
    if (!chartId) { setErr('ยังไม่มี chart ในเคสนี้ — สร้างเอกสารไม่ได้'); return }
    setActiveDef(def); setErr(null)
  }

  async function patchDocuments(nextDocs) {
    const r = await fetch(`/api/charts/${chartId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ document: nextDocs }),
    })
    if (r.status === 401) { redirectToLogin(); return false }
    if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`HTTP ${r.status}: ${t || r.statusText}`) }
    return true
  }

  // Call the renderer; returns the Apps Script result or throws.
  // returnPdf=true also asks for base64 so the preview can render the PDF
  // directly (a clean native viewer, no Drive chrome).
  async function callGenerate(template, data, returnPdf = false) {
    const r = await fetch('/api/documents/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ template, data, return_pdf: returnPdf }),
    })
    if (r.status === 401) { redirectToLogin(); throw new Error('unauthorized') }
    const out = await r.json().catch(() => null)
    if (!r.ok || !out?.success) throw new Error(out?.error || `สร้างเอกสารไม่สำเร็จ (HTTP ${r.status})`)
    return out
  }

  // Move the rendered PDF bytes into Wasabi (chart bucket) and trash the Drive
  // original, then return a chart.document[] entry shaped like an uploaded file
  // (location = Wasabi key → streams via /api/files/{key}, deletes like any file).
  // `out` must carry base64 — always call callGenerate(..., true) before this.
  async function finalizeToWasabi(out, def, note = '') {
    const r = await fetch('/api/documents/finalize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        base64:   out.base64 || '',
        fileId:   out.fileId || '',
        fileName: out.fileName || `${def.label}.pdf`,
        hn,
      }),
    })
    if (r.status === 401) { redirectToLogin(); throw new Error('unauthorized') }
    const fin = await r.json().catch(() => null)
    if (!r.ok || !fin?.success) throw new Error(fin?.error || `ย้ายไฟล์เข้าที่จัดเก็บไม่สำเร็จ (HTTP ${r.status})`)
    return {
      url:        fin.url,          // canonical Wasabi URL
      location:   fin.location,     // Wasabi object key — drives /api/files/{key}
      name:       fin.name,
      type:       fin.type || 'application/pdf',
      size:       fin.size || 0,
      thumbnail:  '',
      doc_type:   def.doc_type,
      template:   def.key,
      generated:  true,
      note,
    }
  }

  // Non-receipt docs: generate (with PDF bytes) → store in Wasabi → attach.
  async function handleGenerate(def, formValues) {
    if (!chartId) { setErr('ยังไม่มี chart ในเคสนี้ — สร้างเอกสารไม่ได้'); return }
    // Sandbox: refused BEFORE Apps Script runs — finalize would be refused anyway
    // (src/lib/training.js), and by then a Drive copy would already exist.
    if (isTraining()) { toast.info(TRAINING_DOC_OFF_TH, { detail: TRAINING_FILES_OFF_DETAIL_TH }); return }
    setBusy(true); setErr(null)
    try {
      const out = await callGenerate(def.key, { ...baseData, ...formValues }, true)
      const entry = await finalizeToWasabi(out, def)
      const ok = await patchDocuments([...docs, entry])
      if (ok) { setActiveDef(null); onSaved?.() }
    } catch (e) {
      if (e?.message !== 'unauthorized') setErr(e?.message || 'สร้างเอกสารไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  // Receipt: generate → open preview. The ReceiptDialog stays mounted under the
  // preview so a reject returns to it with the selection intact.
  async function handleReceiptGenerate(payload, template = 'receipt', note = '') {
    if (!chartId) throw new Error('ยังไม่มี chart ในเคสนี้ — สร้างเอกสารไม่ได้')
    // HTML template: the preview is drawn right here from the payload — no
    // network, nothing created until the user accepts.
    if (htmlReady && isHtmlTemplate(template)) {
      setPreview({ html: renderDocumentHtml(template, payload, browserDocAssets()), template, payload, def: activeDef, note })
      return
    }
    const out = await callGenerate(template, payload, true)
    setPreview({ out, def: activeDef, note })
  }

  // User accepts the previewed PDF → move it into Wasabi (trashing the Drive
  // original) and attach to chart.document[] as ใบเสร็จ.
  async function acceptPreview() {
    if (!preview) return
    // Sandbox: the preview is the lesson; saving it into the chart is a file write.
    if (isTraining()) { toast.info(TRAINING_DOC_OFF_TH, { detail: TRAINING_FILES_OFF_DETAIL_TH }); return }
    setBusy(true); setErr(null)
    try {
      const def = preview.def || DEF_BY_TYPE.receipt
      const entry = preview.html
        ? await renderToWasabi(preview, def)
        : await finalizeToWasabi(preview.out, def, preview.note || '')
      const ok = await patchDocuments([...docs, entry])
      if (ok) { setPreview(null); setActiveDef(null); onSaved?.() }
    } catch (e) {
      if (e?.message !== 'unauthorized') setErr(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  // HTML template accepted → the server renders the SAME template from the same
  // payload, prints it in headless Chrome and stores it in Wasabi in one call.
  async function renderToWasabi({ template, payload, note }, def) {
    const r = await fetch('/api/documents/render', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ template, data: payload, hn }),
    })
    if (r.status === 401) { redirectToLogin(); throw new Error('unauthorized') }
    const out = await r.json().catch(() => null)
    if (!r.ok || !out?.success) throw new Error(out?.error || `สร้าง PDF ไม่สำเร็จ (HTTP ${r.status})`)
    return {
      url:       out.url,
      location:  out.location,
      name:      out.name,
      type:      out.type || 'application/pdf',
      size:      out.size || 0,
      thumbnail: '',
      doc_type:  def.doc_type,
      template:  def.key,
      generated: true,
      note:      note || '',
    }
  }

  // User rejects (wants to edit) → delete the generated Drive PDF, back to dialog.
  async function rejectPreview() {
    if (!preview) return
    // An HTML preview created nothing, so there is nothing to clean up.
    if (preview.html) { setPreview(null); return }
    setBusy(true)
    try {
      await fetch('/api/documents/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fileId: preview.out.fileId }),
      }).catch(() => {})
    } finally {
      setPreview(null)   // ReceiptDialog is still mounted → user edits & re-generates
      setBusy(false)
    }
  }

  const dialogs = (
    <>
      {activeDef && (activeDef.key === 'receipt' || activeDef.key === 'invoice' || activeDef.key === 'fee_detail') && (
        <ReceiptDialog
          def={activeDef}
          variant={activeDef.key}
          baseData={baseData}
          treatment={treatment}
          patient={patient}
          onCancel={() => setActiveDef(null)}
          onGenerate={handleReceiptGenerate}
        />
      )}
      {activeDef && activeDef.key === 'medical_cert' && (
        <MedicalCertDialog
          def={activeDef}
          baseData={baseData}
          treatment={treatment}
          patient={patient}
          chart={chart}
          onCancel={() => setActiveDef(null)}
          onGenerate={handleReceiptGenerate}
        />
      )}
      {activeDef && activeDef.key === 'referral' && (
        <ReferralDialog
          def={activeDef}
          baseData={baseData}
          treatment={treatment}
          patient={patient}
          onCancel={() => setActiveDef(null)}
          onGenerate={handleReceiptGenerate}
        />
      )}
      {activeDef && activeDef.key === 'xray_referral' && (
        <ReferralDialog
          def={activeDef}
          baseData={baseData}
          treatment={treatment}
          patient={patient}
          templateBase="xray_referral"
          bilingual={false}
          subtitle="ประวัติและการตรวจอย่างย่อ"
          fieldLabel="ประวัติและการตรวจอย่างย่อ"
          onCancel={() => setActiveDef(null)}
          onGenerate={handleReceiptGenerate}
        />
      )}
      {activeDef && activeDef.key === 'toothtaxi_checkup' && (
        <ReferralDialog
          def={activeDef}
          baseData={baseData}
          treatment={treatment}
          patient={patient}
          templateBase="toothtaxi_checkup"
          bilingual={false}
          richText={false}
          subtitle="แบบฟอร์มตรวจสุขภาพช่องปาก (พิมพ์แล้วกรอกด้วยมือ)"
          onCancel={() => setActiveDef(null)}
          onGenerate={handleReceiptGenerate}
        />
      )}
      {preview && (
        preview.html ? (
          <HtmlPreviewDialog
            html={preview.html}
            busy={busy}
            onAccept={acceptPreview}
            onReject={rejectPreview}
          />
        ) : (
          <PreviewDialog
            out={preview.out}
            busy={busy}
            onAccept={acceptPreview}
            onReject={rejectPreview}
          />
        )
      )}
      {activeDef && activeDef.key !== 'receipt' && activeDef.key !== 'invoice' && activeDef.key !== 'fee_detail' && activeDef.key !== 'medical_cert' && activeDef.key !== 'referral' && activeDef.key !== 'xray_referral' && activeDef.key !== 'toothtaxi_checkup' && (
        <GenerateDialog
          def={activeDef}
          baseData={baseData}
          busy={busy}
          onCancel={() => setActiveDef(null)}
          onSubmit={(values) => handleGenerate(activeDef, values)}
        />
      )}
    </>
  )

  return { chartId, docs, showBilling, visibleDefs, busy, setBusy, err, setErr, open, patchDocuments, dialogs }
}

export default function DocumentGenerator({ treatment, chart, patient, hn, canEdit, onSaved, uriScreened = false, onScreenURI }) {
  const issuer = useDocumentIssuer({ treatment, chart, patient, hn, onSaved })
  const { chartId, docs, showBilling, visibleDefs, busy, setBusy, err, setErr, patchDocuments } = issuer
  const [confirmDelete, setConfirmDelete] = useState(null)

  // Generated docs shown here. Billing docs (receipt/fee_detail/invoice) are
  // additionally hidden from non-employee (doctor-only) sessions.
  const generated = docs
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => isGeneratedDoc(d))
    .filter(({ d }) => showBilling || !isBillingDoc(d))
  // Split into prominent (most-used) buttons + everything else in a dropdown.
  const primaryDefs = useMemo(
    () => PRIMARY_DOC_KEYS.map((k) => visibleDefs.find((d) => d.key === k)).filter(Boolean),
    [visibleDefs],
  )
  const otherDefs = useMemo(
    () => visibleDefs.filter((d) => !PRIMARY_DOC_KEYS.includes(d.key)),
    [visibleDefs],
  )

  async function handleDelete(idx) {
    setErr(null); setBusy(true)
    const target = docs[idx]
    try {
      // Patch the chart first (drop the row), then best-effort delete the
      // underlying Wasabi object so it doesn't orphan in the bucket.
      const ok = await patchDocuments(docs.filter((_, i) => i !== idx))
      if (!ok) return
      const key = target?.location || target?.key
      if (key) {
        try {
          await fetch(`/api/files/${key.split('/').map(encodeURIComponent).join('/')}`, {
            method: 'DELETE',
            credentials: 'include',
          })
        } catch { /* non-fatal */ }
      }
      onSaved?.()
    } catch (e) {
      setErr(e?.message || 'ลบไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <FileStack size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Documents</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">สร้างเอกสารของเคสนี้ — PDF แนบเข้า chart อัตโนมัติ</div>
        </div>
        {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </header>

      <div className="p-4 sm:p-5 space-y-4">
        {err && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[14px] flex items-start gap-2">
            <AlertTriangle size={14} className="flex-none mt-0.5" />
            <div>{err}</div>
          </div>
        )}

        {/* Create buttons */}
        {canEdit && (
          <div>
            {!chartId && (
              <div className="text-[13.5px] text-slate-400 italic mb-2">ยังไม่มี chart ในเคสนี้ — สร้างเอกสารไม่ได้</div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {primaryDefs.map((def) => (
                <CreateButton
                  key={def.key}
                  icon={def.icon}
                  label={def.label}
                  disabled={!chartId || busy}
                  onClick={() => issuer.open(def)}
                />
              ))}

              {/* Everything else (incl. URI screening) collapses into a searchable
                  dropdown to keep the create row uncluttered. */}
              <OtherDocsMenu
                disabled={!chartId || busy}
                items={[
                  ...otherDefs.map((def) => ({
                    key: def.key,
                    label: def.label,
                    icon: def.icon,
                    onSelect: () => issuer.open(def),
                  })),
                  ...(onScreenURI && !uriScreened
                    ? [{ key: '__uri', label: 'คัดกรอง URI', icon: ShieldCheck, onSelect: () => { onScreenURI(); setErr(null) } }]
                    : []),
                ]}
              />
            </div>
          </div>
        )}

        {/* Generated documents on this visit */}
        <div>
          <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-2">
            เอกสารที่สร้างแล้ว {generated.length > 0 && <span className="text-slate-300">· {generated.length}</span>}
          </div>
          {generated.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-[14px] text-slate-400">
              ยังไม่มีเอกสารที่สร้างในเคสนี้
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50/50 divide-y divide-slate-100 overflow-hidden">
              {generated.map(({ d, i }) => {
                const def = DEF_BY_TYPE[d.doc_type]
                const Icon = def?.icon || FileText
                // Wasabi-backed docs stream via /api/files/{key}; fall back to the
                // raw url/download_url for any legacy Drive-based entries.
                const href = d.location
                  ? `/api/files/${d.location.split('/').map(encodeURIComponent).join('/')}`
                  : (d.url || '')
                const downloadHref = href || d.download_url || ''
                return (
                  <div key={`${d.location || d.drive_file_id || d.url || i}-${i}`} className="px-3.5 py-2.5 flex items-center gap-3 hover:bg-white transition-colors">
                    <span className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 flex items-center justify-center flex-none">
                      <Icon size={15} strokeWidth={1.9} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-medium text-slate-800 truncate">{def?.label || d.name || 'เอกสาร'}</div>
                      <div className="text-[12.5px] text-slate-400 truncate">{d.name || ''}</div>
                    </div>
                    {href && (
                      <a href={href} target="_blank" rel="noopener noreferrer"
                        className="text-slate-400 hover:text-brand-600 p-1.5 rounded-md hover:bg-brand-50 transition-colors" title="เปิดเอกสาร">
                        <ExternalLink size={14} strokeWidth={2} />
                      </a>
                    )}
                    {downloadHref && (
                      <a href={downloadHref} target="_blank" rel="noopener noreferrer"
                        className="text-slate-400 hover:text-brand-600 p-1.5 rounded-md hover:bg-brand-50 transition-colors" title="ดาวน์โหลด">
                        <Download size={14} strokeWidth={2} />
                      </a>
                    )}
                    {canEdit && (
                      <button type="button" onClick={() => setConfirmDelete(i)} disabled={busy}
                        className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50 disabled:opacity-50" title="ลบออกจาก chart">
                        <Trash2 size={13} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {issuer.dialogs}

      {confirmDelete != null && (
        <ConfirmDialog
          title="ลบเอกสารนี้ออกจาก chart?"
          message="รายการจะถูกลบออกจาก chart (ไฟล์ต้นฉบับใน Google Drive ยังคงอยู่)"
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={() => { const idx = confirmDelete; setConfirmDelete(null); handleDelete(idx) }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Create-row controls                                                        */
/* -------------------------------------------------------------------------- */

function CreateButton({ icon: Icon, label, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="group flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-50/60 hover:bg-brand-50 ring-1 ring-transparent hover:ring-brand-200 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 group-hover:text-brand-600 flex items-center justify-center flex-none transition-colors">
        <Icon size={15} strokeWidth={1.9} />
      </span>
      <span className="text-[14px] font-medium text-slate-700 group-hover:text-brand-700 leading-tight">{label}</span>
      <Plus size={13} className="ml-auto text-slate-300 group-hover:text-brand-500 flex-none" strokeWidth={2.25} />
    </button>
  )
}

// Searchable dropdown of the less-used documents (+ URI screening).
function OtherDocsMenu({ items, disabled }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)

  const needle = q.trim().toLowerCase()
  const filtered = needle
    ? (items || []).filter((it) => it.label.toLowerCase().includes(needle))
    : (items || [])

  const ta = useTypeahead({
    count: filtered.length,
    onSelect: (i) => { filtered[i].onSelect(); setOpen(false); setQ('') },
    onClose: () => setOpen(false),
  })

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    if (open) {
      document.addEventListener('mousedown', onDoc)
      document.addEventListener('keydown', onKey)
    }
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!items || items.length === 0) return null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`group w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl ring-1 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
          open ? 'bg-brand-50 ring-brand-200' : 'bg-slate-50/60 ring-transparent hover:bg-brand-50 hover:ring-brand-200'
        }`}
      >
        <span className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 group-hover:text-brand-600 flex items-center justify-center flex-none transition-colors">
          <LayoutGrid size={15} strokeWidth={1.9} />
        </span>
        <span className="text-[14px] font-medium text-slate-700 group-hover:text-brand-700 leading-tight">เอกสารอื่นๆ</span>
        <ChevronDown size={14} strokeWidth={2.25} className={`ml-auto text-slate-400 flex-none transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1.5 rounded-xl bg-white shadow-xl shadow-slate-900/10 ring-1 ring-slate-200 overflow-hidden animate-ss-pop-in">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={13} strokeWidth={2} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={ta.onKeyDown}
                placeholder="ค้นหาเอกสาร…"
                className="w-full pl-8 pr-2.5 py-1.5 text-[14px] border border-slate-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 outline-none bg-white placeholder:text-slate-400"
              />
            </div>
          </div>
          <div ref={ta.listRef} className="max-h-64 overflow-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-center text-[13.5px] text-slate-400">ไม่พบเอกสาร</div>
            ) : (
              filtered.map((it, i) => {
                const Icon = it.icon || FileText
                return (
                  <button
                    key={it.key}
                    type="button"
                    data-ti-index={i}
                    onMouseEnter={() => ta.setActiveIndex(i)}
                    onClick={() => { it.onSelect(); setOpen(false); setQ('') }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                      i === ta.activeIndex ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-7 h-7 rounded-lg bg-slate-50 ring-1 ring-slate-200 text-slate-500 flex items-center justify-center flex-none">
                      <Icon size={14} strokeWidth={1.9} />
                    </span>
                    <span className="text-[14px] font-medium text-slate-700 leading-tight">{it.label}</span>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Generate dialog                                                            */
/* -------------------------------------------------------------------------- */

function GenerateDialog({ def, baseData, busy, onCancel, onSubmit }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(def.fields.map((f) => [f.name, f.default || ''])),
  )

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const Icon = def.icon
  const set = (name, v) => setValues((s) => ({ ...s, [name]: v }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={onCancel} />
      <div className="relative w-full max-w-[500px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5 animate-ss-pop-in max-h-[88vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200">
            <Icon size={16} strokeWidth={2} />
          </span>
          <div className="flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900">{def.label}</div>
            <div className="text-[13.5px] text-slate-500">สร้างเอกสาร PDF และแนบเข้าเคสนี้</div>
          </div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100">
            <X size={14} strokeWidth={2.25} />
          </button>
        </div>

        {/* Auto-filled context (read-only preview) */}
        <div className="rounded-xl bg-slate-50/70 ring-1 ring-slate-100 px-3.5 py-2.5 mb-4 text-[13.5px] text-slate-500 space-y-0.5">
          <div><span className="text-slate-400">ผู้ป่วย:</span> <span className="font-medium text-slate-700">{baseData.patient_name || '—'}</span> {baseData.hn && <span className="font-mono text-slate-400">· HN {baseData.hn}</span>}</div>
          <div><span className="text-slate-400">วันที่รักษา:</span> <span className="text-slate-600">{/* screen = ค.ศ.; treatment_date_th is the PRINTED (พ.ศ.) value */}{baseData.treatment_date ? formatThaiDate(baseData.treatment_date) : '—'}</span></div>
          {baseData.doctor_name && <div><span className="text-slate-400">ทันตแพทย์:</span> <span className="text-slate-600">{baseData.doctor_name}</span></div>}
        </div>

        <div className="space-y-3">
          {def.fields.map((f) => (
            <div key={f.name}>
              <label className="block text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  value={values[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  placeholder={f.placeholder || ''}
                  rows={2}
                  className="w-full px-2.5 py-1.5 text-[14px] border border-slate-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 outline-none bg-white placeholder:text-slate-400 resize-y min-h-[44px]"
                />
              ) : (
                <input
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  value={values[f.name]}
                  onChange={(e) => set(f.name, e.target.value)}
                  placeholder={f.placeholder || ''}
                  className="w-full px-2.5 py-1.5 text-[14.5px] border border-slate-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 outline-none bg-white placeholder:text-slate-400"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">
            ยกเลิก
          </button>
          <button type="button" onClick={() => onSubmit(values)} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} strokeWidth={2.5} />}
            {busy ? 'กำลังสร้าง…' : 'สร้างเอกสาร'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Receipt dialog — billing-driven, with include toggles + เบิกย้อนหลัง mode    */
/* -------------------------------------------------------------------------- */

const BRANCH_SEL = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[13.5px] font-medium text-slate-700 outline-none focus:ring-2 focus:ring-brand-200 cursor-pointer max-w-full'

function ReceiptDialog({ def, variant = 'receipt', baseData, treatment, patient, onCancel, onGenerate }) {
  const isFeeDetail = variant === 'fee_detail'
  const tid = treatment?.id ?? treatment?.treatment_id
  const pid = patient?.id ?? patient?.patient_id ?? ''
  // The branch the visit happened at — the default for the document header.
  const treatmentBranchId = String(treatment?.branch_id ?? treatment?.branch?.id ?? '')
  const me = useMe()

  const [mode, setMode]       = useState('normal')        // 'normal' | 'advance'
  const [lang, setLang]       = useState('th')            // 'th' | 'en'
  const [rows, setRows]       = useState(null)            // null = loading
  const [visitCount, setVisitCount] = useState(1)         // treatments merged into this document
  const [loadErr, setLoadErr] = useState(null)
  const [excluded, setExcluded] = useState(() => new Set())
  // Which branch's letterhead the PDF carries. Defaults to the treatment's
  // branch. Overridable ONLY on a เบิกย้อนหลัง ใบเสร็จ/ใบแจ้งหนี้: the 12 branches
  // sit under 5 DIFFERENT juristic entities (company name / tax id / address /
  // licence / ประกันสังคม number all move with the branch), and a claim
  // sometimes has to be issued under the branch holding the registration rather
  // than the one that treated. A ปกติ receipt has no such reason, so it does not
  // get the ability to print another company's tax id.
  const [branchId, setBranchId] = useState(treatmentBranchId)
  const [aux, setAux] = useState({ branch: null, branches: [], appts: [], branchMap: {}, branchMapEN: {}, doctor: null, employee: null, doctorMap: {}, nextApptDoctor: null })
  const [gen, setGen] = useState(false)
  const [genErr, setGenErr] = useState(null)
  // วิธีชำระเงิน is pulled from the treatment — not user-editable.
  const paymentMethod = pickPaymentMethod(treatment)

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  useEffect(() => {
    let alive = true
    setRows(null); setLoadErr(null)
    // `GET /billing` returns SOFT-DELETED rows (`active:false`) — Xano's
    // DELETE /billing/{id} only flips the flag. Without this filter a line
    // voided in Bubble (or, later, from การรักษา) still offers itself as a
    // printable item on a ใบเสร็จ. A saved receipt is permanent fraud
    // evidence, so printing a voided line is an integrity defect, not a
    // cosmetic one. `/reports/reimbursement` filters the same endpoint the
    // same way (Reimbursement.jsx#bIsActive).
    // ONE document per visit: every treatment of this patient on the same day,
    // with the same doctor, at the same branch is billed on this receipt (Ram,
    // 2026-10-15) — the counter splits a visit into several treatments, the
    // patient paid once.
    setVisitCount(1)
    sameVisitTreatmentIds(treatment, pid)
      .then((ids) => Promise.all(ids.map((id) =>
        apiGet('/api/billing', { treatment_id: id, patient_id: pid, per_page: 200 })
          .then((res) => asList(res).map((b) => ({ ...b, _tid: id }))),
      )).then((lists) => ({ ids, lists })))
      .then(({ ids, lists }) => {
        if (!alive) return
        // Keyed by billing id, so a line can never be printed twice even if two
        // treatment queries were to return it.
        const seen = new Set()
        const merged = []
        lists.flat().forEach((b, i) => {
          if (b.active === false) return
          const key = String(b.id ?? b.billing_id ?? `${b._tid}-${i}`)
          if (seen.has(key)) return
          seen.add(key)
          merged.push({ ...b, _key: key })
        })
        setVisitCount(ids.length)
        setRows(merged.sort(compareBillingLines))
      })
      .catch((e) => { if (alive) { setLoadErr(e?.message || 'โหลด billing ไม่สำเร็จ'); setRows([]) } })
    return () => { alive = false }
  }, [tid, pid, treatment])

  // Branch (+ its company), patient appointments, and a branch id→name map —
  // needed to fill the receipt payload at generate time.
  useEffect(() => {
    let alive = true
    const bid = treatment?.branch_id ?? treatment?.branch?.id
    const did = treatment?.doctor_id ?? treatment?.doctor?.id
    const eid = me?.employee_id
    async function load() {
      const settled = await Promise.allSettled([
        bid ? apiGet(`/api/branches/${bid}`) : Promise.resolve(null),
        pid ? apiGet('/api/appointments', { patient_id: pid, per_page: 100 }) : Promise.resolve(null),
        apiGet('/api/branches'),
        did ? apiGet(`/api/doctors/${did}`) : Promise.resolve(null),
        eid ? apiGet(`/api/employees/${eid}`) : Promise.resolve(null),
        apiGet('/api/doctors'),
      ])
      if (!alive) return
      const [bRes, aRes, blRes, dRes, eRes, dlRes] = settled
      const branch   = bRes.status === 'fulfilled' ? bRes.value : null
      const appts    = aRes.status === 'fulfilled' ? asList(aRes.value) : []
      const list     = blRes.status === 'fulfilled' ? asList(blRes.value) : []
      const doctor   = dRes.status === 'fulfilled' ? dRes.value : null
      const employee = eRes.status === 'fulfilled' ? eRes.value : null
      const docList  = dlRes.status === 'fulfilled' ? asList(dlRes.value) : []
      const branchMap = {}, branchMapEN = {}
      let branchFromList = null
      const bidStr = String(bid ?? '')
      for (const br of list) {
        const id = String(br.id ?? br.branch_id)
        branchMap[id]   = br.name || br.branch_name || ''
        branchMapEN[id] = br.nameEng || br.name_eng || br.name_en || br.nameEN || br.branch_name_eng || br.name || br.branch_name || ''
        if (id === bidStr) branchFromList = br
      }
      const doctorMap = {}
      for (const dd of docList) doctorMap[String(dd.id ?? dd.doctor_id)] = dd

      // The next appointment's doctor may differ from the treatment's. Resolve its
      // full record (which has name_eng) via the single /doctor/{id} endpoint —
      // most reliable for the English name — reusing the treatment doctor / list
      // when possible.
      let nextApptDoctor = null
      const r = resolveNextAppt(appts, treatment, branchMap, doctor)
      if (r) {
        const ad = r.a.doctor || {}
        const nextDocId = (ad.id ?? ad.doctor_id) ?? r.a.doctor_id
        if (nextDocId != null && String(nextDocId) !== '') {
          if (String(nextDocId) === String(did) && doctor) nextApptDoctor = doctor
          else if (doctorMap[String(nextDocId)]) nextApptDoctor = doctorMap[String(nextDocId)]
          else { try { nextApptDoctor = await apiGet(`/api/doctors/${nextDocId}`) } catch { /* non-fatal */ } }
        }
      }

      if (!alive) return
      // Prefer the list record for the treatment's branch — it carries the full
      // Company object with English fields (nameEN / addressEN); the single
      // /branch/{id} fetch may not.
      setAux({ branch: branchFromList || branch, branches: list, appts, branchMap, branchMapEN, doctor, employee, doctorMap, nextApptDoctor })
    }
    load()
    return () => { alive = false }
  }, [treatment, pid, me?.employee_id])

  // Header-branch options: every branch from the list (they carry the full
  // Company object + licence/ประกันสังคม numbers), ordered by branch id. If the
  // list fetch failed the picker is hidden and the header falls back to the
  // treatment's branch exactly as before.
  const branchOptions = useMemo(() => {
    const list = [...(aux.branches || [])].sort((a, b) => Number(a.id ?? a.branch_id ?? 0) - Number(b.id ?? b.branch_id ?? 0))
    // A treatment at a branch missing from the list would otherwise leave the
    // <select> showing someone else's letterhead while `value` matched nothing.
    if (aux.branch && !list.some((b) => String(b.id ?? b.branch_id) === treatmentBranchId)) list.unshift(aux.branch)
    return list
  }, [aux.branches, aux.branch, treatmentBranchId])

  // Gate + fallback in one: switching back to ปกติ (or opening the fee-detail
  // variant) reverts the header to the treatment's branch without discarding
  // what the user picked, so flipping the toggle back restores it.
  const canPickBranch = !isFeeDetail && mode === 'advance'
  const headerBranchId = canPickBranch ? String(branchId || '') : treatmentBranchId

  const headerBranch = useMemo(() => {
    const id = String(headerBranchId || treatmentBranchId || '')
    return branchOptions.find((b) => String(b.id ?? b.branch_id) === id)
      || (id === treatmentBranchId ? aux.branch : null)
  }, [branchOptions, aux.branch, headerBranchId, treatmentBranchId])

  const branchOverridden = headerBranchId !== treatmentBranchId
  const treatmentBranchName = aux.branchMap?.[treatmentBranchId] || ''
  const headerBranchName = (headerBranch && (headerBranch.name || headerBranch.branch_name)) || aux.branchMap?.[headerBranchId] || ''
  const headerCompany = (headerBranch && (headerBranch.Company || headerBranch.company)) || {}
  const headerHint = [
    headerCompany.name || headerCompany.company_name || '',
    headerBranch?.license_number ? `ใบอนุญาต ${headerBranch.license_number}` : '',
    headerBranch?.ssonumber ? `ประกันสังคม ${headerBranch.ssonumber}` : '',
  ].filter(Boolean).join(' · ')

  // Zero-amount lines (ค่าธรรมเนียม 0, the internal clinic↔doctor carve-out rows)
  // are not offered at all: they never print, so listing them only padded the
  // "รวม (N รายการ)" count with lines that are not on the document.
  const visible = useMemo(() => {
    const all = (rows || []).filter((b) => Math.abs(lineAmount(b, mode)) > 0.005)
    return mode === 'advance' ? all.filter(hasAdvance) : all
  }, [rows, mode])

  const included = visible.filter((b) => !excluded.has(b._key))
  const total = included.reduce((acc, b) => acc + lineAmount(b, mode), 0)
  const noBilling = rows != null && rows.length === 0

  function toggle(key) {
    setExcluded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n })
  }

  async function submit() {
    setGenErr(null); setGen(true)
    try {
      let payload, template
      if (isFeeDetail) {
        payload = buildFeeDetailPayload({
          baseData, treatment, patient, me, paymentMethod, included, lang,
          branch: headerBranch, headerBranchId, branchMap: aux.branchMap, branchMapEN: aux.branchMapEN,
          doctor: aux.doctor, employee: aux.employee,
        })
        template = lang === 'en' ? 'fee_detail_en' : 'fee_detail'
      } else {
        payload = buildReceiptPayload({
          baseData, treatment, patient, me, mode, paymentMethod, included, lang,
          branch: headerBranch, headerBranchId, appts: aux.appts, branchMap: aux.branchMap, branchMapEN: aux.branchMapEN,
          doctor: aux.doctor, employee: aux.employee, doctorMap: aux.doctorMap, nextApptDoctor: aux.nextApptDoctor,
        })
        // Invoice reuses every receipt rule — only the template wording differs.
        const base = variant === 'invoice' ? 'invoice' : 'receipt'
        template = lang === 'en' ? `${base}_en` : base
      }
      // A document issued under someone else's letterhead says so on the chart
      // file row — otherwise the override leaves no trace anywhere.
      await onGenerate(payload, template, branchOverridden ? `ออกในนามสาขา ${headerBranchName}` : '')
    } catch (e) {
      if (e?.message !== 'unauthorized') setGenErr(e?.message || 'สร้างเอกสารไม่สำเร็จ')
    } finally {
      setGen(false)
    }
  }

  const Icon = def.icon
  const busy = gen
  const canSubmit = !busy && included.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={onCancel} />
      <div className="relative w-full max-w-[560px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5 animate-ss-pop-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200">
            <Icon size={16} strokeWidth={2} />
          </span>
          <div className="flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900">{def.label}</div>
            <div className="text-[13.5px] text-slate-500">{isFeeDetail ? 'เลือกรายการค่ารักษาที่จะแสดงในเอกสาร' : `เลือกรายการที่จะออก${def.label}`}</div>
          </div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100">
            <X size={14} strokeWidth={2.25} />
          </button>
        </div>

        {/* Context */}
        <div className="rounded-xl bg-slate-50/70 ring-1 ring-slate-100 px-3.5 py-2.5 mb-4 text-[13.5px] text-slate-500 space-y-0.5">
          <div><span className="text-slate-400">ผู้ป่วย:</span> <span className="font-medium text-slate-700">{baseData.patient_name || '—'}</span> {baseData.hn && <span className="font-mono text-slate-400">· HN {baseData.hn}</span>}</div>
          <div><span className="text-slate-400">วันที่รักษา:</span> <span className="text-slate-600">{/* screen = ค.ศ.; treatment_date_th is the PRINTED (พ.ศ.) value */}{baseData.treatment_date ? formatThaiDate(baseData.treatment_date) : '—'}</span></div>
          <div>
            <span className="text-slate-400">วิธีชำระเงิน:</span>{' '}
            {paymentMethod
              ? <span className="font-medium text-slate-700">{paymentMethod}</span>
              : <span className="text-slate-400 italic">— ไม่ระบุในเคสนี้ —</span>}
            <span className="text-slate-300"> · จากข้อมูลการรักษา</span>
          </div>
          {/* Which branch's letterhead the PDF carries — company name, tax id,
              address, licence and ประกันสังคม number all come from it. เบิกย้อนหลัง
              only: that is the case where the claim may belong to another
              branch's registration. */}
          {canPickBranch && (
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <span className="text-slate-400">สาขาในหัวเอกสาร:</span>
            {branchOptions.length > 0 ? (
              <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={BRANCH_SEL}>
                {/* A treatment with no branch_id keeps `value` matching an option —
                    otherwise the box would DISPLAY branch #1 while sending nothing. */}
                {!treatmentBranchId && <option value="">— ไม่ระบุสาขา —</option>}
                {branchOptions.map((b) => {
                  const id = String(b.id ?? b.branch_id)
                  return (
                    <option key={id} value={id}>
                      {(b.name || b.branch_name || `#${id}`) + (id === treatmentBranchId ? ' · สาขาที่รักษา' : '')}
                    </option>
                  )
                })}
              </select>
            ) : (
              <span className="font-medium text-slate-700">{headerBranchName || '—'}</span>
            )}
            {branchOverridden && (
              <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-amber-600">
                <AlertTriangle size={12} strokeWidth={2.25} />
                ไม่ใช่สาขาที่รักษา ({treatmentBranchName || '—'})
              </span>
            )}
          </div>
          )}
          {canPickBranch && headerHint && <div className="text-[12.5px] text-slate-400 truncate">{headerHint}</div>}
        </div>

        {/* Mode + language switches */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {!isFeeDetail && (
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
              {[
                { key: 'normal',  label: 'ปกติ' },
                { key: 'advance', label: 'เบิกย้อนหลัง' },
              ].map((m) => {
                const on = mode === m.key
                return (
                  <button key={m.key} type="button" onClick={() => setMode(m.key)}
                    className={`px-3 py-1 rounded-md text-[13.5px] font-semibold transition-colors ${
                      on ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200' : 'text-slate-500 hover:text-slate-900'
                    }`}>{m.label}</button>
                )
              })}
            </div>
          )}
          <span className="text-[12.5px] text-slate-400">
            {isFeeDetail ? 'รายการค่ารักษา · ราคา × จำนวน' : (mode === 'advance' ? 'แสดงเฉพาะรายการที่มีเบิกย้อนหลัง · ใช้ราคา advance' : 'ราคา × จำนวน')}
          </span>

          {/* Language toggle (TH / EN template) */}
          <div className="ml-auto inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
            {[
              { key: 'th', label: 'ไทย' },
              { key: 'en', label: 'EN' },
            ].map((l) => {
              const on = lang === l.key
              return (
                <button key={l.key} type="button" onClick={() => setLang(l.key)}
                  className={`px-3 py-1 rounded-md text-[13.5px] font-semibold transition-colors ${
                    on ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200' : 'text-slate-500 hover:text-slate-900'
                  }`}>{l.label}</button>
              )
            })}
          </div>
        </div>

        {visitCount > 1 && (
          <div className="mb-3 rounded-lg bg-brand-50/70 ring-1 ring-brand-100 px-3 py-2 text-[13px] text-brand-800">
            รวมรายการจาก <b>{visitCount} การรักษา</b> ในวันเดียวกัน · ทันตแพทย์และสาขาเดียวกัน — ออกเป็น{def.label}ฉบับเดียว
          </div>
        )}

        {/* Billing list */}
        {rows == null ? (
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3.5 py-3">
                <div className="w-4 h-4 bg-slate-100 rounded animate-pulse" />
                <div className="flex-1 h-3.5 bg-slate-100 rounded animate-pulse" />
                <div className="w-16 h-3.5 bg-slate-100 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : loadErr ? (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[14px] flex items-start gap-2">
            <AlertTriangle size={14} className="flex-none mt-0.5" /><div>{loadErr}</div>
          </div>
        ) : noBilling ? (
          <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-[14px] text-slate-400">
            ไม่พบรายการ billing ของเคสนี้ — ออก{def.label}ไม่ได้
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-[14px] text-slate-400">
            {mode === 'advance' ? 'ไม่มีรายการที่มีเบิกย้อนหลังในเคสนี้' : 'ไม่มีรายการที่มียอดเงินในเคสนี้'}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {visible.map((b) => {
              const on = !excluded.has(b._key)
              const amt = lineAmount(b, mode)
              return (
                <button
                  type="button"
                  key={b._key}
                  onClick={() => toggle(b._key)}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors ${on ? 'hover:bg-slate-50' : 'bg-slate-50/40'}`}
                >
                  <span className={`w-[18px] h-[18px] rounded-[5px] flex items-center justify-center flex-none ring-1 transition-colors ${
                    on ? 'bg-brand-600 ring-brand-600 text-white' : 'bg-white ring-slate-300 text-transparent'
                  }`}>
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[14px] font-medium truncate ${on ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{itemName(b)}</div>
                    <div className="text-[12.5px] text-slate-400">{lineSub(b, mode)}</div>
                  </div>
                  <div className={`text-[14px] font-semibold tabular-nums flex-none ${on ? 'text-slate-700' : 'text-slate-300 line-through'}`}>
                    {formatMoney(amt)}
                  </div>
                </button>
              )
            })}
            {/* Total */}
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50/70">
              <span className="text-[13.5px] font-semibold text-slate-500">รวม ({included.length} รายการ)</span>
              <span className="text-[16px] font-bold text-slate-900 tabular-nums">{formatMoney(total)} ฿</span>
            </div>
          </div>
        )}

        {genErr && (
          <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[14px] flex items-start gap-2">
            <AlertTriangle size={14} className="flex-none mt-0.5" /><div>{genErr}</div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button type="button" onClick={submit} disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} strokeWidth={2.5} />}
            {busy ? 'กำลังสร้าง…' : `สร้าง${def.label}`}
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Preview dialog — read the generated PDF, accept (save) or reject (delete)   */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Medical certificate dialog — 3 free-text fields + TH/EN, DB-derived context  */
/* -------------------------------------------------------------------------- */

function MedicalCertDialog({ def, baseData, treatment, patient, chart, onCancel, onGenerate }) {
  const me = useMe()
  const [lang, setLang] = useState('th')
  // Default the diagnosis/examination field to the chart's chief complaint.
  const [diagnosis, setDiagnosis] = useState(() => chartChiefComplaint(chart))
  const [treatmentText, setTreatmentText] = useState('')
  const [opinion, setOpinion] = useState('')
  const [aux, setAux] = useState({ branch: null, branchMap: {}, branchMapEN: {}, doctor: null, employee: null })
  const [gen, setGen] = useState(false)
  const [genErr, setGenErr] = useState(null)

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  useEffect(() => {
    let alive = true
    const bid = treatment?.branch_id ?? treatment?.branch?.id
    const did = treatment?.doctor_id ?? treatment?.doctor?.id
    const eid = me?.employee_id
    Promise.allSettled([
      apiGet('/api/branches'),
      did ? apiGet(`/api/doctors/${did}`) : Promise.resolve(null),
      eid ? apiGet(`/api/employees/${eid}`) : Promise.resolve(null),
    ]).then(([blRes, dRes, eRes]) => {
      if (!alive) return
      const list     = blRes.status === 'fulfilled' ? asList(blRes.value) : []
      const doctor   = dRes.status === 'fulfilled' ? dRes.value : null
      const employee = eRes.status === 'fulfilled' ? eRes.value : null
      const branchMap = {}, branchMapEN = {}
      let branchFromList = null
      const bidStr = String(bid ?? '')
      for (const br of list) {
        const id = String(br.id ?? br.branch_id)
        branchMap[id]   = br.name || br.branch_name || ''
        branchMapEN[id] = br.nameEng || br.name_eng || br.name_en || br.nameEN || br.name || br.branch_name || ''
        if (id === bidStr) branchFromList = br
      }
      setAux({ branch: branchFromList, branchMap, branchMapEN, doctor, employee })
    })
    return () => { alive = false }
  }, [treatment, me?.employee_id])

  async function submit() {
    setGenErr(null); setGen(true)
    try {
      const payload = buildMedicalCertPayload({
        baseData, treatment, patient, me, lang,
        diagnosis, treatmentText, opinion,
        branch: aux.branch, branchMap: aux.branchMap, branchMapEN: aux.branchMapEN,
        doctor: aux.doctor, employee: aux.employee,
      })
      await onGenerate(payload, lang === 'en' ? 'medical_cert_en' : 'medical_cert')
    } catch (e) {
      if (e?.message !== 'unauthorized') setGenErr(e?.message || 'สร้างเอกสารไม่สำเร็จ')
    } finally {
      setGen(false)
    }
  }

  const Icon = def.icon
  const ta  = 'w-full mt-1.5 px-2.5 py-1.5 text-[14px] border border-slate-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 outline-none bg-white placeholder:text-slate-400 resize-y min-h-[60px]'
  const lbl = 'text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={onCancel} />
      <div className="relative w-full max-w-[560px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5 animate-ss-pop-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200"><Icon size={16} strokeWidth={2} /></span>
          <div className="flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900">{def.label}</div>
            <div className="text-[13.5px] text-slate-500">กรอกผลตรวจ · การรักษา · ความเห็น</div>
          </div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"><X size={14} strokeWidth={2.25} /></button>
        </div>

        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-[13.5px] text-slate-500">ผู้ป่วย: <span className="font-medium text-slate-700">{baseData.patient_name || '—'}</span>{baseData.hn && <span className="font-mono text-slate-400"> · HN {baseData.hn}</span>}</div>
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
            {[{ key: 'th', label: 'ไทย' }, { key: 'en', label: 'EN' }].map((l) => {
              const on = lang === l.key
              return <button key={l.key} type="button" onClick={() => setLang(l.key)} className={`px-3 py-1 rounded-md text-[13.5px] font-semibold transition-colors ${on ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200' : 'text-slate-500 hover:text-slate-900'}`}>{l.label}</button>
            })}
          </div>
        </div>

        {genErr && <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[14px] mb-3">{genErr}</div>}

        <div className="space-y-3">
          <div><div className={lbl}>ผลการตรวจ/การวินิจฉัย</div><textarea className={ta} rows={3} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="ผลการตรวจ / การวินิจฉัย" /></div>
          <div><div className={lbl}>ให้การรักษาโดย</div><textarea className={ta} rows={3} value={treatmentText} onChange={(e) => setTreatmentText(e.target.value)} placeholder="รายละเอียดการรักษา" /></div>
          <div><div className={lbl}>ความเห็นของทันตแพทย์</div><textarea className={ta} rows={3} value={opinion} onChange={(e) => setOpinion(e.target.value)} placeholder="ความเห็น / คำแนะนำ" /></div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button type="button" onClick={submit} disabled={gen} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {gen ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} strokeWidth={2.5} />} สร้างเอกสาร
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Referral dialog — one rich-text field (refer_detail) + TH/EN, DB context     */
/* -------------------------------------------------------------------------- */

function ReferralDialog({ def, baseData, treatment, patient, onCancel, onGenerate, templateBase = 'referral', bilingual = true, richText = true, subtitle = 'รายละเอียดประเด็นส่งตัว / ปรึกษา', fieldLabel = 'รายละเอียดประเด็นส่งตัว/ปรึกษา' }) {
  const me = useMe()
  const [lang, setLang] = useState('th')
  const [referDetail, setReferDetail] = useState('')
  const [aux, setAux] = useState({ branch: null, branchMap: {}, branchMapEN: {}, doctor: null, employee: null })
  const [gen, setGen] = useState(false)
  const [genErr, setGenErr] = useState(null)

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  useEffect(() => {
    let alive = true
    const bid = treatment?.branch_id ?? treatment?.branch?.id
    const did = treatment?.doctor_id ?? treatment?.doctor?.id
    const eid = me?.employee_id
    Promise.allSettled([
      apiGet('/api/branches'),
      did ? apiGet(`/api/doctors/${did}`) : Promise.resolve(null),
      eid ? apiGet(`/api/employees/${eid}`) : Promise.resolve(null),
    ]).then(([blRes, dRes, eRes]) => {
      if (!alive) return
      const list     = blRes.status === 'fulfilled' ? asList(blRes.value) : []
      const doctor   = dRes.status === 'fulfilled' ? dRes.value : null
      const employee = eRes.status === 'fulfilled' ? eRes.value : null
      const branchMap = {}, branchMapEN = {}
      let branchFromList = null
      const bidStr = String(bid ?? '')
      for (const br of list) {
        const id = String(br.id ?? br.branch_id)
        branchMap[id]   = br.name || br.branch_name || ''
        branchMapEN[id] = br.nameEng || br.name_eng || br.name_en || br.nameEN || br.name || br.branch_name || ''
        if (id === bidStr) branchFromList = br
      }
      setAux({ branch: branchFromList, branchMap, branchMapEN, doctor, employee })
    })
    return () => { alive = false }
  }, [treatment, me?.employee_id])

  async function submit() {
    setGenErr(null); setGen(true)
    try {
      const payload = buildReferralPayload({
        baseData, treatment, patient, me, lang, referDetail,
        branch: aux.branch, branchMap: aux.branchMap, branchMapEN: aux.branchMapEN,
        doctor: aux.doctor, employee: aux.employee,
      })
      await onGenerate(payload, bilingual && lang === 'en' ? `${templateBase}_en` : templateBase)
    } catch (e) {
      if (e?.message !== 'unauthorized') setGenErr(e?.message || 'สร้างเอกสารไม่สำเร็จ')
    } finally {
      setGen(false)
    }
  }

  const Icon = def.icon
  const lbl = 'text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={onCancel} />
      <div className="relative w-full max-w-[560px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5 animate-ss-pop-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200"><Icon size={16} strokeWidth={2} /></span>
          <div className="flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900">{def.label}</div>
            <div className="text-[13.5px] text-slate-500">{subtitle}</div>
          </div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"><X size={14} strokeWidth={2.25} /></button>
        </div>

        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-[13.5px] text-slate-500">ผู้ป่วย: <span className="font-medium text-slate-700">{baseData.patient_name || '—'}</span>{baseData.hn && <span className="font-mono text-slate-400"> · HN {baseData.hn}</span>}</div>
          {bilingual && (
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
              {[{ key: 'th', label: 'ไทย' }, { key: 'en', label: 'EN' }].map((l) => {
                const on = lang === l.key
                return <button key={l.key} type="button" onClick={() => setLang(l.key)} className={`px-3 py-1 rounded-md text-[13.5px] font-semibold transition-colors ${on ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200' : 'text-slate-500 hover:text-slate-900'}`}>{l.label}</button>
              })}
            </div>
          )}
        </div>

        {genErr && <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[14px] mb-3">{genErr}</div>}

        {richText ? (
          <div>
            <div className={lbl + ' mb-1.5'}>{fieldLabel}</div>
            <RichTextEditor value={referDetail} onChange={setReferDetail} placeholder="พิมพ์รายละเอียด · ใช้ **ตัวหนา**, รายการ ฯลฯ ได้" minHeight={140} />
          </div>
        ) : (
          <div className="text-[14px] text-slate-500 rounded-xl bg-slate-50/70 ring-1 ring-slate-100 px-3.5 py-3">
            เอกสารนี้จะกรอกข้อมูลผู้ป่วย / ทันตแพทย์ / วันที่ ให้อัตโนมัติ — รายละเอียดการตรวจกรอกด้วยมือบนเอกสารที่พิมพ์
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button type="button" onClick={submit} disabled={gen} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {gen ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} strokeWidth={2.5} />} สร้างเอกสาร
          </button>
        </div>
      </div>
    </div>
  )
}

function PreviewDialog({ out, busy, onAccept, onReject }) {
  // Render the PDF from base64 (clean native viewer, no Drive chrome). Fall back
  // to the Drive embed only if base64 wasn't returned.
  const [blobUrl, setBlobUrl] = useState('')
  const [zoom, setZoom] = useState('fit')   // 'fit' | number(%)
  const [expanded, setExpanded] = useState(false)   // dialog fills the viewport
  useEffect(() => {
    if (!out?.base64) { setBlobUrl(''); return }
    let url = ''
    try { url = base64ToPdfUrl(out.base64) } catch { url = '' }
    setBlobUrl(url)
    setZoom('fit')
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [out])

  // For the base64 viewer we control zoom via the PDF open-parameter; the Drive
  // fallback embed has its own controls. The iframe is keyed on the full src so
  // it remounts on zoom change (changing only the #fragment won't re-apply).
  const zoomParam = zoom === 'fit' ? 'view=FitH' : `zoom=${zoom}`
  const previewSrc = blobUrl
    ? `${blobUrl}#toolbar=0&navpanes=0&${zoomParam}`
    : (out?.fileId ? `https://drive.google.com/file/d/${out.fileId}/preview` : '')
  const zoomIn  = () => setZoom((z) => (z === 'fit' ? 125 : Math.min(250, z + 25)))
  const zoomOut = () => setZoom((z) => (z === 'fit' ? 75 : Math.max(50, z - 25)))

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center ${expanded ? 'p-3' : 'px-4 py-6'}`} role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px] animate-ss-pop-in" />
      <div className={`relative w-full h-full ${expanded ? 'max-w-none max-h-none' : 'max-w-[680px] max-h-[92vh]'} bg-white rounded-2xl shadow-2xl shadow-slate-900/30 ring-1 ring-slate-200 flex flex-col animate-ss-pop-in overflow-hidden`}>
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200">
            <ReceiptText size={16} strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-slate-900 truncate">ตรวจสอบใบเสร็จก่อนบันทึก</div>
            <div className="text-[13.5px] text-slate-500 truncate">{out?.fileName || 'receipt.pdf'}</div>
          </div>
          {out?.url && (
            <a href={out.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13.5px] font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200 transition-colors flex-none">
              <ExternalLink size={13} strokeWidth={2} /> เปิดแท็บใหม่
            </a>
          )}
        </div>

        <div className="flex-1 min-h-0 bg-slate-100">
          {previewSrc
            ? <iframe key={previewSrc} title="receipt-preview" src={previewSrc} className="w-full h-full border-0" />
            : <div className="w-full h-full flex items-center justify-center text-slate-400 text-[14px]">ไม่สามารถแสดงตัวอย่างได้</div>}
        </div>

        <div className="flex items-center gap-2 px-5 py-3.5 border-t border-slate-100">
          {blobUrl && (
            <div className="inline-flex items-center gap-1.5 flex-none">
              <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
                <button type="button" onClick={zoomOut} title="ซูมออก"
                  className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 rounded-l-lg disabled:opacity-40"
                  disabled={zoom !== 'fit' && zoom <= 50}>
                  <Minus size={13} strokeWidth={2.5} />
                </button>
                <button type="button" onClick={() => setZoom('fit')} title="พอดีหน้า"
                  className="px-1.5 py-1.5 text-[13px] tabular-nums text-slate-600 w-12 text-center select-none hover:bg-slate-50">
                  {zoom === 'fit' ? 'พอดี' : `${zoom}%`}
                </button>
                <button type="button" onClick={zoomIn} title="ซูมเข้า"
                  className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 rounded-r-lg disabled:opacity-40"
                  disabled={zoom !== 'fit' && zoom >= 250}>
                  <Plus size={13} strokeWidth={2.5} />
                </button>
              </div>
              <button type="button" onClick={() => { setExpanded((e) => !e); setZoom('fit') }}
                title={expanded ? 'ย่อหน้าต่าง' : 'ขยายเต็มจอ'}
                className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${
                  expanded ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}>
                {expanded ? <Minimize2 size={13} strokeWidth={2.25} /> : <Maximize2 size={13} strokeWidth={2.25} />}
              </button>
            </div>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onReject} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            <X size={13} strokeWidth={2.25} /> แก้ไขข้อมูล
          </button>
          <button type="button" onClick={onAccept} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} strokeWidth={2.5} />}
            ยอมรับ & บันทึก
          </button>
        </div>
      </div>
    </div>
  )
}

// Preview of an HTML-template document, drawn in the browser from the template
// itself — instant, and identical to the PDF the server prints on accept. The
// iframe is sandboxed WITHOUT allow-scripts (the template has none; nothing in
// a patient's name can run), but keeps same-origin so we can set its zoom.
const SHEET_PX = 210 * 96 / 25.4 + 48   // A4 width at 96 dpi + the preview's side gutter

function HtmlPreviewDialog({ html, busy, onAccept, onReject }) {
  const frameRef = useRef(null)
  const [zoom, setZoom] = useState('fit')   // 'fit' | number(%)
  const [expanded, setExpanded] = useState(false)   // dialog fills the viewport

  // Apply zoom inside the iframe's document. 'fit' = the sheet fills the width.
  const applyZoom = () => {
    const f = frameRef.current
    const root = f?.contentDocument?.documentElement
    if (!f || !root) return
    const z = zoom === 'fit' ? Math.min(1.5, (f.clientWidth - 8) / SHEET_PX) : zoom / 100
    root.style.zoom = String(z)
  }
  useEffect(applyZoom)   // every render: zoom changed, or the frame resized
  useEffect(() => {
    const f = frameRef.current
    if (!f || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => applyZoom())
    ro.observe(f)
    return () => ro.disconnect()
  })

  const zoomIn  = () => setZoom((z) => (z === 'fit' ? 100 : Math.min(200, z + 25)))
  const zoomOut = () => setZoom((z) => (z === 'fit' ? 50 : Math.max(50, z - 25)))

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center ${expanded ? 'p-3' : 'px-4 py-6'}`} role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px] animate-ss-pop-in" />
      <div className={`relative w-full h-full ${expanded ? 'max-w-none max-h-none' : 'max-w-[760px] max-h-[92vh]'} bg-white rounded-2xl shadow-2xl shadow-slate-900/30 ring-1 ring-slate-200 flex flex-col animate-ss-pop-in overflow-hidden`}>
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200">
            <ReceiptText size={16} strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-slate-900 truncate">ตรวจสอบเอกสารก่อนบันทึก</div>
            <div className="text-[13.5px] text-slate-500 truncate">PDF จะถูกสร้างและแนบเข้า chart เมื่อกดยอมรับ</div>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-slate-200">
          <iframe ref={frameRef} title="document-preview" srcDoc={html} sandbox="allow-same-origin"
            onLoad={applyZoom} className="w-full h-full border-0" />
        </div>

        <div className="flex items-center gap-2 px-5 py-3.5 border-t border-slate-100">
          <div className="inline-flex items-center gap-1.5 flex-none">
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
              <button type="button" onClick={zoomOut} title="ซูมออก"
                className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 rounded-l-lg disabled:opacity-40"
                disabled={zoom !== 'fit' && zoom <= 50}>
                <Minus size={13} strokeWidth={2.5} />
              </button>
              <button type="button" onClick={() => setZoom('fit')} title="พอดีความกว้าง"
                className="px-1.5 py-1.5 text-[13px] tabular-nums text-slate-600 w-12 text-center select-none hover:bg-slate-50">
                {zoom === 'fit' ? 'พอดี' : `${zoom}%`}
              </button>
              <button type="button" onClick={zoomIn} title="ซูมเข้า"
                className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 rounded-r-lg disabled:opacity-40"
                disabled={zoom !== 'fit' && zoom >= 200}>
                <Plus size={13} strokeWidth={2.5} />
              </button>
            </div>
            <button type="button" onClick={() => { setExpanded((e) => !e); setZoom('fit') }}
              title={expanded ? 'ย่อหน้าต่าง' : 'ขยายเต็มจอ'}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${
                expanded ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}>
              {expanded ? <Minimize2 size={13} strokeWidth={2.25} /> : <Maximize2 size={13} strokeWidth={2.25} />}
            </button>
          </div>
          <div className="flex-1" />
          <button type="button" onClick={onReject} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            <X size={13} strokeWidth={2.25} /> แก้ไขข้อมูล
          </button>
          <button type="button" onClick={onAccept} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} strokeWidth={2.5} />}
            {busy ? 'กำลังสร้าง PDF…' : 'ยอมรับ & บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Billing helpers — defensive field access (camel/snake variants)            */
/* -------------------------------------------------------------------------- */

function num(v) {
  const n = Number(String(v ?? '').replace(/[, ฿]/g, ''))
  return isNaN(n) ? 0 : n
}
function itemName(b) {
  const bt = b.billing_type
  return (bt && (bt.name || bt.detail || bt.title))
    || b.name || b.detail || b.title || b.note || 'รายการ'
}
function unitOf(b)  { return num(b.unit ?? b.quantity ?? b.qty ?? 1) || 1 }
function priceOf(b) { return num(b.price ?? b.unit_price ?? 0) }
function normalLine(b)  { return b.priceXunit != null ? num(b.priceXunit) : priceOf(b) * unitOf(b) }
function advanceLine(b) { return num(b.priceAdvance ?? b.priceadvance ?? b.price_advance ?? 0) }
function lineAmount(b, mode) { return mode === 'advance' ? advanceLine(b) : normalLine(b) }

// Clinic / doctor split of a billing line.
function priceClinic(b) { return num(b.priceClinic ?? b.priceclinic ?? b.price_clinic ?? 0) }
function priceDoctor(b) { return num(b.priceDoctor ?? b.pricedoctor ?? b.price_doctor ?? 0) }

// Receipt rows are grouped by `billing_type.receipt_type` (the receipt
// category). It may also sit directly on the billing line; may be a scalar
// (string/id) or an expanded relation object — handle all of these.
function receiptTypeRaw(b) {
  const bt = b.billing_type ?? b.billingType
  if (bt && typeof bt === 'object' && bt.receipt_type != null && bt.receipt_type !== '') return bt.receipt_type
  return b.receipt_type ?? b.receiptType ?? null
}
function receiptGroupKey(b) {
  const rt = receiptTypeRaw(b)
  if (rt == null || rt === '') return '__ungrouped__'
  if (typeof rt === 'object') return String(rt.id ?? rt.name ?? rt.detail ?? JSON.stringify(rt))
  return String(rt)
}
function receiptGroupName(b) {
  const rt = receiptTypeRaw(b)
  if (rt && typeof rt === 'object') {
    const n = String(rt.name ?? rt.detail ?? rt.title ?? rt.label ?? '').trim()
    if (n) return n
  } else if (rt != null && String(rt).trim() !== '') {
    return String(rt).trim()
  }
  return itemName(b) // fallback when receipt_type has no display value
}
// English line-item label from billing_type.receipt_typeEN; falls back to Thai.
function receiptTypeRawEN(b) {
  const bt = b.billing_type ?? b.billingType
  if (bt && typeof bt === 'object' && bt.receipt_typeEN != null && bt.receipt_typeEN !== '') return bt.receipt_typeEN
  return b.receipt_typeEN ?? b.receiptTypeEN ?? null
}
function receiptGroupNameEN(b) {
  const rt = receiptTypeRawEN(b)
  if (rt && typeof rt === 'object') {
    const n = String(rt.name ?? rt.detail ?? rt.title ?? rt.label ?? '').trim()
    if (n) return n
  } else if (rt != null && String(rt).trim() !== '') {
    return String(rt).trim()
  }
  return receiptGroupName(b) // fallback to the Thai receipt_type name
}
function advanceRaw(b) { return b.advancePay ?? b.advancepay ?? b.advance_pay }
function hasAdvance(b) {
  const v = advanceRaw(b)
  return v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '0'
}
function lineSub(b, mode) {
  if (mode === 'advance') {
    const adv = advanceRaw(b)
    return `เบิกย้อนหลัง${adv && String(adv) !== '1' && String(adv) !== 'true' ? ` · ${adv}` : ''}`
  }
  const u = unitOf(b), p = priceOf(b)
  if (p > 0) return `${formatMoney(p)} × ${u}`
  return ''
}
function formatMoney(n) {
  return num(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}
function pickPaymentMethod(t) {
  if (!t) return ''
  const v = t.paymentmethod ?? t.payment_method ?? t.paymentMethod ?? t.payment
  if (v == null) return ''
  if (typeof v === 'object') return String(v.name || v.label || v.type || v.value || '').trim()
  return String(v).trim()
}

/* -------------------------------------------------------------------------- */
/* Receipt payload assembly                                                   */
/* -------------------------------------------------------------------------- */

const THAI_MONTHS_SHORT_DG = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// All prices are displayed as XX,XXX.XX (always 2 decimals).
function formatBaht2(n) {
  return num(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function thaiShortDate(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')} ${THAI_MONTHS_SHORT_DG[d.getMonth()]} ${d.getFullYear() + 543}`
}

// "วันพุธที่ 4 มีนาคม 2569" — formatThaiDate's wording with the Buddhist-era year.
function thaiLongDateBE(v) {
  const out = formatThaiDate(v)
  return out.replace(/ (\d{4})$/, (_, y) => ` ${Number(y) + 543}`)
}

// HH:mm on the clinic's clock, whatever the staff device's timezone is.
function bkkTime(d) {
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' })
}

// ref_num = current_year-current_month-current_date-treatment_id (Gregorian).
function buildRefNum(treatment) {
  const tid = treatment?.id ?? treatment?.treatment_id ?? ''
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}-${tid}`
}

function pickAddress(p) {
  if (!p) return ''
  const a = p.address
  if (a && typeof a === 'object') return formatThaiAddress(a)
  if (typeof a === 'string' && a.trim()) return a.trim()
  for (const k of ['full_address', 'addr', 'address1', 'patient_address', 'home_address']) {
    if (typeof p[k] === 'string' && p[k].trim()) return p[k].trim()
  }
  return ''
}

// Xano patient.address = { line1, subdistrict, district, province, postcode }.
// Bangkok uses แขวง/เขต; other provinces use ตำบล/อำเภอ.
function formatThaiAddress(a) {
  const line1 = a.line1 || a.line || a.address1 || a.detail || ''
  const sub = a.subdistrict || a.tambon || ''
  const dist = a.district || a.amphoe || ''
  const prov = a.province || ''
  const post = a.postcode || a.zipcode || a.zip || ''
  const bkk = ['กรุงเทพมหานคร', 'กรุงเทพฯ', 'กทม.', 'กทม'].includes(String(prov).trim())
  const subLabel = sub ? (bkk ? 'แขวง' : 'ตำบล') + sub : ''
  const distLabel = dist ? (bkk ? 'เขต' : 'อำเภอ') + dist : ''
  return [line1, subLabel, distLabel, prov, post].filter(Boolean).join(' ').trim()
}

// English address. Prefers explicit English fields if the record has them;
// otherwise falls back to the stored values in a comma-separated English
// structure with the province mapped (e.g. กรุงเทพมหานคร → Bangkok).
const PROVINCE_EN = { 'กรุงเทพมหานคร': 'Bangkok', 'กรุงเทพฯ': 'Bangkok', 'กทม.': 'Bangkok', 'กทม': 'Bangkok' }
function pickAddressEN(p) {
  if (!p) return ''
  for (const k of ['addressEng', 'address_eng', 'address_en', 'addressEN', 'full_address_eng']) {
    if (typeof p[k] === 'string' && p[k].trim()) return p[k].trim()
  }
  const a = p.address
  if (a && typeof a === 'object') return formatEnglishAddress(a)
  if (typeof a === 'string' && a.trim()) return a.trim()
  return pickAddress(p) // last resort — Thai
}
function formatEnglishAddress(a) {
  const line1 = a.line1_eng || a.line1_en || a.line1 || a.line || a.address1 || a.detail || ''
  const sub = a.subdistrict_eng || a.subdistrict_en || a.subdistrict || a.tambon || ''
  const dist = a.district_eng || a.district_en || a.district || a.amphoe || ''
  const provRaw = a.province_eng || a.province_en || a.province || ''
  const prov = PROVINCE_EN[String(provRaw).trim()] || provRaw
  const post = a.postcode || a.zipcode || a.zip || ''
  const tail = [sub, dist, [prov, post].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  return [line1, tail].filter(Boolean).join(', ').trim()
}

function pickDoctorLicense(treatment, doctor) {
  const d = doctor || treatment?.doctor || {}
  return String(
    d.license_number ?? d.license_no ?? d.license ?? d.dentist_license
    ?? d.licence ?? d.no_license ?? d.license_id ?? d.license_num ?? d.number ?? '',
  ).trim()
}

function msOf(v) {
  if (v == null || v === '') return NaN
  const n = Number(v)
  if (!isNaN(n)) return n          // Unix ms
  const d = new Date(v)
  return isNaN(d.getTime()) ? NaN : d.getTime()
}

function hhmm(ms) {
  if (isNaN(ms)) return ''
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' }).format(new Date(ms))
  } catch { return '' }
}

function ddmmyyyyBE(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear() + 543}`
}

function normalizeHex(c) {
  if (typeof c !== 'string') return ''
  const h = c.replace('#', '')
  return /^[0-9a-fA-F]{6}$/.test(h) ? `#${h}` : ''
}

// Resolve the next upcoming patient appointment → the raw bits both the Thai and
// English builders format. Returns null when there's no upcoming appointment.
function resolveNextAppt(appts, treatment, branchMap, fullDoctor) {
  const now = Date.now()
  const upcoming = (appts || [])
    .filter((a) => !a.event_type || String(a.event_type).trim() === '')   // patient appts only
    .map((a) => ({ a, t: msOf(a.start_date ?? a.date) }))
    .filter((x) => !isNaN(x.t) && x.t >= now)
    .sort((x, y) => x.t - y.t)
  if (upcoming.length === 0) return null

  const a = upcoming[0].a
  const branchName = (a.branch && (a.branch.name || a.branch.branch_name))
    || (branchMap && branchMap[String(a.branch_id)]) || ''
  // Doctor: prefer the appointment's doctor; fall back to the full doctor record
  // when it's the same doctor as the treatment.
  const apptDoc = a.doctor || {}
  const apptId = String(apptDoc.id ?? apptDoc.doctor_id ?? '')
  const treatId = String(treatment?.doctor_id ?? treatment?.doctor?.id ?? '')
  const sameDoc = apptId && treatId && apptId === treatId
  const rich = (apptDoc.name || apptDoc.surname || apptDoc.name_eng) ? apptDoc : (sameDoc && fullDoctor ? fullDoctor : apptDoc)
  return { ms: upcoming[0].t, a, branchName, rich }
}

// Thai next-appointment block → คุณ<firstName> มีนัด… / สาขา… กับทันตแพทย์…
function buildNextAppointmentData(appts, firstName, branchMap, fullDoctor, treatment) {
  const r = resolveNextAppt(appts, treatment, branchMap, fullDoctor)
  if (!r) return { has: false, patient_name: firstName }
  return {
    has: true,
    patient_name: firstName,
    branch_name: r.branchName,
    date: thaiShortDate(new Date(r.ms)),
    time: hhmm(msOf(r.a.start_date) || r.ms),
    doctor_name: pickDoctorName(r.rich),
  }
}

// English next-appointment block. Resolves the English branch name (from the
// branches list / GET branch) and the appointment doctor's English name by
// looking up the full doctor record (GET doctor) by id — the appointment embed
// only carries the Thai name.
function buildNextAppointmentDataEN(appts, patientNameEN, branchMap, fullDoctor, treatment, branchMapEN, doctorMap, nextApptDoctor) {
  const r = resolveNextAppt(appts, treatment, branchMap, fullDoctor)
  if (!r) return { has: false, patient_name: patientNameEN }
  const a = r.a
  const branchEN = (branchMapEN && branchMapEN[String(a.branch_id)])
    || (a.branch && (a.branch.nameEng || a.branch.name_eng || a.branch.name_en || a.branch.nameEN))
    || r.branchName
  // Prefer the targeted full doctor record (fetched in aux — has name_eng), then
  // the doctors-list map, then the appointment's Thai embed.
  const apptDoc = a.doctor || {}
  const apptDocId = String((apptDoc.id ?? apptDoc.doctor_id) ?? a.doctor_id ?? '')
  const fullAppt = nextApptDoctor || (doctorMap && apptDocId && doctorMap[apptDocId]) || r.rich
  return {
    has: true,
    patient_name: patientNameEN,
    branch_name: branchEN,
    date: engShortDate(new Date(r.ms)),
    time: hhmm(msOf(a.start_date) || r.ms),
    doctor_name: pickDoctorNameEN(fullAppt, treatment),
  }
}

/* -------------------------------------------------------------------------- */
/* English-variant helpers (for the receipt_en template)                      */
/* -------------------------------------------------------------------------- */

const ENG_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function engShortDate(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')} ${ENG_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
}

// Patient English name = name_eng + surname_eng; fall back to the Thai name.
function pickPatientNameEN(p) {
  if (!p) return ''
  const en = [p.name_eng ?? p.name_en ?? p.nameEN, p.surname_eng ?? p.surname_en ?? p.surnameEN]
    .map((x) => String(x ?? '').trim()).filter(Boolean).join(' ').trim()
  if (en) return en
  return [p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || ''
}
function pickDoctorNameEN(doctor, treatment) {
  const d = doctor || treatment?.doctor || {}
  const en = [d.name_eng ?? d.name_en ?? d.nameEN, d.surname_eng ?? d.surname_en ?? d.surnameEN]
    .map((x) => String(x ?? '').trim()).filter(Boolean).join(' ').trim()
  if (en) return en
  return pickDoctorFullname(doctor, treatment) // fallback Thai
}
function pickEmployeeNameEN(e) {
  if (!e) return ''
  const en = [e.name_eng ?? e.name_en ?? e.nameEN, e.surname_eng ?? e.surname_en ?? e.surnameEN]
    .map((x) => String(x ?? '').trim()).filter(Boolean).join(' ').trim()
  if (en) return en
  return pickEmployeeName(e) // fallback Thai
}
function companyNameEN(c) {
  return String(c?.name_eng ?? c?.name_en ?? c?.nameEN ?? c?.company_name_eng ?? c?.name ?? c?.company_name ?? '').trim()
}
function companyAddressEN(c) {
  return String(c?.address_eng ?? c?.address_en ?? c?.addressEN ?? c?.address ?? c?.addr ?? c?.full_address ?? '').trim()
}

// Baht amount → English words, e.g. 3,099.00 → "Three thousand ninety-nine baht".
function bahtTextEN(amount) {
  const n = num(amount)
  const neg = n < 0
  const abs = Math.abs(n)
  const baht = Math.floor(abs)
  const satang = Math.round((abs - baht) * 100)
  let out = baht > 0 ? `${enWords(baht)} baht` : 'zero baht'
  if (satang > 0) out += ` and ${enWords(satang)} satang`
  out = out.charAt(0).toUpperCase() + out.slice(1)
  return (neg ? 'Minus ' : '') + out
}
function enWords(n0) {
  let n = Math.floor(Math.abs(n0))
  if (n === 0) return 'zero'
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']
  const under1000 = (x) => {
    let s = ''
    if (x >= 100) { s += ones[Math.floor(x / 100)] + ' hundred'; x %= 100; if (x) s += ' ' }
    if (x >= 20) { s += tens[Math.floor(x / 10)]; if (x % 10) s += '-' + ones[x % 10] }
    else if (x > 0) s += ones[x]
    return s
  }
  const groups = []
  while (n > 0) { groups.push(n % 1000); n = Math.floor(n / 1000) }
  const scales = ['', 'thousand', 'million', 'billion', 'trillion']
  const parts = []
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue
    parts.push(under1000(groups[i]) + (scales[i] ? ' ' + scales[i] : ''))
  }
  return parts.join(' ')
}

// Doctor's ชื่อ-สกุล without prefix (for the "กับทันตแพทย์ …" line).
function pickDoctorName(d) {
  if (!d) return ''
  const ns = [d.name, d.surname].filter(Boolean).join(' ').trim()
  if (ns) return ns
  return String(d.fullname || d.full_name || d.code || '').trim()
}

// Employee's Thai full name (ชื่อ-สกุล) from the employee record.
function pickEmployeeName(e) {
  if (!e) return ''
  const ns = [e.name, e.surname].filter(Boolean).join(' ').trim()
  if (ns) return ns
  return String(e.fullname || e.full_name || '').trim()
}

// base64 PDF → object URL for the preview iframe (native viewer, no Drive UI).
function base64ToPdfUrl(b64) {
  const bin = atob(b64)
  const arr = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
  return URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }))
}

// The medical-certificate / referral templates render `{{doctor_prefix}}{{doctor_fullname}}`,
// but `doctor_fullname` already carries the prefix ("ทพ. ธนิตย์ …") — sending both
// doubles it ("ทพ.ทพ. ธนิตย์ …"). Only emit a prefix when the full name lacks one.
function doctorPrefixToken(docObj, fullname) {
  const prefix = String(docObj?.prefix ?? '').trim()
  if (!prefix) return ''
  const norm = (s) => String(s).replace(/[\s.·]/g, '')
  if (norm(fullname).startsWith(norm(prefix))) return ''
  return prefix + ' '
}

// Doctor's real full name (not the short code) — from the full doctor record.
function pickDoctorFullname(doctor, treatment) {
  const d = doctor || {}
  if (d.fullname) return String(d.fullname).trim()
  const parts = [d.prefix, d.name, d.surname].filter(Boolean).join(' ').trim()
  if (parts) return parts
  const td = treatment?.doctor || {}
  return String(td.fullname || td.full_name || td.name || td.code || '').trim()
}

// Every treatment that belongs on the same document as `treatment`: this
// patient, the same Bangkok calendar day, the same doctor, the same branch.
// Ordered by time, so the receipt reads in visit order. Fails SOFT to just this
// treatment — a receipt short of a sibling is caught at the counter; a receipt
// that will not open is not.
async function sameVisitTreatmentIds(treatment, pid) {
  const tid = String(treatment?.id ?? treatment?.treatment_id ?? '')
  const day = bkkDay(treatment?.date)
  const doc = doctorIdOf(treatment)
  const br = branchIdOf(treatment)
  if (!pid || !day || !doc || !br) return [tid]
  let all
  try { all = await fetchAllTreatments(pid) } catch { return [tid] }
  const same = all.filter((t) => t && t.active !== false
    && bkkDay(t.date) === day && doctorIdOf(t) === doc && branchIdOf(t) === br)
  const ids = same
    .sort((a, b) => (msOf(a.date) - msOf(b.date)) || (Number(a.id ?? a.treatment_id) - Number(b.id ?? b.treatment_id)))
    .map((t) => String(t.id ?? t.treatment_id))
  return ids.includes(tid) ? ids : [tid, ...ids]
}

// Order of lines on the dialog AND the document (Ram, 2026-10-15): by billing
// type, so the same kind of item sits together, then in the order it was billed.
// The per-visit ค่าบริการทางการแพทย์ และค่าบริการปลอดเชื้อเครื่องมือแพทย์ always
// goes last. It is billing_type 238, the same id the "ค่าบริการทางการแพทย์" shortcut
// writes (src/lib/billingShortcuts.js). The name check catches a row whose type
// did not come back expanded.
const STERILE_FEE_TYPE_ID = '238'
function billingTypeIdOf(b) {
  const bt = b?.billing_type ?? b?.billingType
  if (bt && typeof bt === 'object') return String(bt.id ?? '')
  return String(b?.billing_type_id ?? bt ?? '')
}
function isSterileFee(b) {
  return billingTypeIdOf(b) === STERILE_FEE_TYPE_ID || /^ค่าบริการทางการแพทย์\s*และค่าบริการปลอดเชื้อ/.test(itemName(b))
}
function compareBillingLines(a, b) {
  const fa = isSterileFee(a) ? 1 : 0
  const fb = isSterileFee(b) ? 1 : 0
  if (fa !== fb) return fa - fb
  const ta = Number(billingTypeIdOf(a)) || Infinity
  const tb = Number(billingTypeIdOf(b)) || Infinity
  if (ta !== tb) return ta - tb
  return (Number(a.id ?? a.billing_id) || 0) - (Number(b.id ?? b.billing_id) || 0)
}

// The treatment list carries doctor / branch either as an id field, an expanded
// object, or a bare id in the relation field — accept all three.
function refId(t, field) {
  const direct = t?.[`${field}_id`]
  if (direct != null && direct !== '') return String(direct)
  const rel = t?.[field]
  if (rel && typeof rel === 'object') return String(rel.id ?? rel[`${field}_id`] ?? '')
  return rel == null ? '' : String(rel)
}
const doctorIdOf = (t) => refId(t, 'doctor')
const branchIdOf = (t) => refId(t, 'branch')

// Receipt rows, layout 'items' (the default): every included billing line as
// billed — name + line amount (price × unit, or the advance price on a
// เบิกย้อนหลัง receipt), so the receipt reads line-for-line like the visit's
// Billing. Zero-amount lines are left off: they are the internal clinic↔doctor
// carve-out lines (price 0), which mean nothing to a patient. Total = Σ of all
// included lines, the same figure the dialog shows.
function itemizedReceiptRows(included, mode) {
  const shown = included.filter((b) => Math.abs(lineAmount(b, mode)) > 0.005)
  const treatments = shown.map((b, i) => ({
    no: String(i + 1),
    treatment_list:   itemName(b),
    treatment_listEN: itemNameEN(b),
    treatment_price:  formatBaht2(lineAmount(b, mode)),
  }))
  const total = included.reduce((acc, b) => acc + lineAmount(b, mode), 0)
  return { treatments, total }
}

// Receipt rows, layout 'grouped' — the original receipt: lines merged by
// receipt_type, the dentist's fee as one ค่าผู้ประกอบวิชาชีพ row. NOT reachable
// from the UI since 2026-10-15 (Ram: every receipt lists each billing line), but
// kept on request — to bring it back, pass layout: 'grouped' to
// buildReceiptPayload.
function groupedReceiptRows(included, mode) {
  // Build the receipt rows. The patient must NOT see the internal clinic↔doctor
  // reallocation (negative clinic on price-0 carve-out lines), but the doctor's
  // professional fee MUST be itemized separately (tax). So:
  //   1. Group billing lines by `billing_type.receipt_type`; each group's clinic
  //      amount = Σ priceClinic (ปกติ) or Σ (priceAdvance − priceDoctor) (advance).
  //   2. Fold every non-positive group (pure doctor carve-out) into the positive
  //      groups, largest first — preserves the sum, removes all negatives.
  //   3. Append one positive row "ค่าผู้ประกอบวิชาชีพ ทันตกรรม" = Σ priceDoctor.
  const EPS = 0.005
  const groups = []
  const groupIdx = new Map()
  let doctorTotal = 0
  for (const b of included) {
    doctorTotal += priceDoctor(b)
    const amt = mode === 'advance' ? advanceLine(b) - priceDoctor(b) : priceClinic(b)
    const key = receiptGroupKey(b)
    if (!groupIdx.has(key)) {
      groupIdx.set(key, groups.length)
      groups.push({ name: receiptGroupName(b), nameEN: receiptGroupNameEN(b), amount: 0 })
    }
    groups[groupIdx.get(key)].amount += amt
  }

  // Positive groups stay as line items; the (negative) carve-out pool is absorbed
  // into them largest-first so no row ever goes below zero.
  const positives = groups.filter((g) => g.amount > EPS).sort((a, b) => b.amount - a.amount)
  let negPool = groups.reduce((acc, g) => acc + (g.amount > EPS ? 0 : g.amount), 0) // ≤ 0
  for (const g of positives) {
    if (negPool >= -EPS) break
    const absorb = Math.min(g.amount, -negPool)
    g.amount -= absorb
    negPool += absorb
  }
  const clinicRows = positives.filter((g) => g.amount > EPS)

  const treatments = clinicRows.map((g, i) => ({
    no: String(i + 1),
    treatment_list: g.name,
    treatment_listEN: g.nameEN,
    treatment_price: formatBaht2(g.amount),
  }))
  if (doctorTotal > EPS) {
    treatments.push({
      no: String(treatments.length + 1),
      treatment_list: 'ค่าผู้ประกอบวิชาชีพ ทันตกรรม',
      treatment_listEN: 'Professional Dental Fee',
      treatment_price: formatBaht2(doctorTotal),
    })
  }

  const total = clinicRows.reduce((acc, g) => acc + g.amount, 0) + doctorTotal
  return { treatments, total }
}

// `headerBranchId` is the branch chosen in the dialog for the LETTERHEAD; it
// defaults to the treatment's branch. It only names the fallback for the branch
// name — `next_appointment` still prints the appointment's own branch.
function buildReceiptPayload({ baseData, treatment, patient, me, mode, layout = 'items', paymentMethod, included, branch, headerBranchId, appts, branchMap, branchMapEN, doctor, employee, doctorMap, nextApptDoctor, lang = 'th' }) {
  const company = (branch && (branch.Company || branch.company)) || {}
  const headerId = String(headerBranchId || treatment?.branch_id || treatment?.branch?.id || '')
  const branchName = (branch && (branch.name || branch.branch_name))
    || (branchMap && branchMap[headerId]) || ''
  const branchNameEN = (branch && (branch.nameEng || branch.name_eng || branch.name_en || branch.nameEN || branch.branch_name_eng))
    || (branchMapEN && branchMapEN[headerId]) || branchName
  const { treatments, total } = layout === 'grouped'
    ? groupedReceiptRows(included, mode)
    : itemizedReceiptRows(included, mode)
  const now = new Date()

  return {
    fullname:  baseData.patient_name || '',
    HN:        baseData.hn || '',
    address:   lang === 'en' ? pickAddressEN(patient) : pickAddress(patient),
    doctor_fullname: pickDoctorFullname(doctor, treatment) || baseData.doctor_name || '',
    doctor_license:  pickDoctorLicense(treatment, doctor),
    company_name:      company.name || company.company_name || '',
    company_taxnumber: String(company.tax_number ?? company.taxnumber ?? company.tax_id ?? company.taxid ?? company.tax_no ?? '').trim(),
    company_address:   String(company.address ?? company.addr ?? company.address1 ?? company.full_address ?? company.location ?? '').trim(),
    branch_name:       branchName,
    branch_license_number: String(branch?.license_number ?? branch?.branch_license_number ?? branch?.license ?? branch?.license_no ?? '').trim(),
    branch_ssonumber:      String(branch?.sso_number ?? branch?.ssonumber ?? branch?.sso ?? branch?.sso_no ?? branch?.sso_number_text ?? '').trim(),
    ref_num:   buildRefNum(treatment),
    employee_fullname: pickEmployeeName(employee) || (me?.name || me?.email || '').trim(),
    current_date: lang === 'en' ? engShortDate(new Date()) : thaiShortDate(new Date()),
    // วันที่รับบริการ — the visit's own date, which the HTML receipt prints (the
    // issue date is not what a patient or insurer reads a receipt for).
    service_date:   treatment?.date ? thaiShortDate(new Date(treatment.date)) : '',
    service_dateEN: treatment?.date ? engShortDate(new Date(treatment.date)) : '',
    next_appointment: buildNextAppointmentData(appts, (patient?.name || '').trim() || baseData.patient_name || '', branchMap, doctor, treatment),
    treatments,
    treatment_price_total: formatBaht2(total),
    treatment_price_totalThai: bahtText(total),
    payment_method: paymentMethod,
    billing_mode: mode === 'advance' ? 'เบิกย้อนหลัง' : 'ปกติ',
    ...billingTrace(included, treatment, now),

    // ── English variants (filled into the receipt_en template's *EN tokens) ──
    fullnameEN:          pickPatientNameEN(patient),
    branch_nameEN:       branchNameEN,
    doctor_fullnameEN:   pickDoctorNameEN(doctor, treatment),
    doctor_fullnameEn:   pickDoctorNameEN(doctor, treatment), // template uses this casing
    company_nameEN:      companyNameEN(company),
    company_addressEN:   companyAddressEN(company),
    employee_fullnameEN: pickEmployeeNameEN(employee) || (me?.name || me?.email || '').trim(),
    current_dateEN:      engShortDate(new Date()),
    treatment_price_totalEN: bahtTextEN(total),
    next_appointmentEN:  buildNextAppointmentDataEN(appts, pickPatientNameEN(patient), branchMap, doctor, treatment, branchMapEN, doctorMap, nextApptDoctor),
  }
}

// English line-item name for the fee-detail (ungrouped) rows — prefers an
// English billing_type name, else falls back to the Thai item name.
function itemNameEN(b) {
  const bt = b.billing_type
  if (bt && typeof bt === 'object') {
    const en = bt.name_eng || bt.nameEng || bt.name_en || bt.nameEN || bt.detail_eng || bt.receipt_typeEN
    if (en && String(en).trim()) return String(en).trim()
  }
  const direct = b.name_eng || b.nameEN
  if (direct && String(direct).trim()) return String(direct).trim()
  return itemName(b)
}

// Treatment-fee detail — same header as the receipt, but the billing table is
// NOT grouped (one row per selected line at priceXunit) and there is no
// next-appointment block. Emits both treatment_price_total and price_total (+
// Thai/EN word forms) so either token name in the template fills.
// Footer trace line of the HTML billing documents: every treatment the
// document bills (a visit can span several) + when it was printed. Stamped
// here, not in the template, so the preview and the PDF the server prints from
// this same payload show the same time.
function billingTrace(included, treatment, now) {
  const ids = [...new Set(included.map((b) => String(b._tid ?? '')).filter(Boolean))]
  if (!ids.length && (treatment?.id ?? treatment?.treatment_id)) ids.push(String(treatment.id ?? treatment.treatment_id))
  return {
    treatment_ids: ids.join(', '),
    printed_at:   `${thaiShortDate(now)} ${bkkTime(now)} น.`,
    printed_atEN: `${engShortDate(now)} ${bkkTime(now)}`,
  }
}

function buildFeeDetailPayload({ baseData, treatment, patient, me, paymentMethod, included, branch, headerBranchId, branchMap, branchMapEN, doctor, employee, lang = 'th' }) {
  const company = (branch && (branch.Company || branch.company)) || {}
  const headerId = String(headerBranchId || treatment?.branch_id || treatment?.branch?.id || '')
  const branchName = (branch && (branch.name || branch.branch_name))
    || (branchMap && branchMap[headerId]) || ''
  const branchNameEN = (branch && (branch.nameEng || branch.name_eng || branch.name_en || branch.nameEN || branch.branch_name_eng))
    || (branchMapEN && branchMapEN[headerId]) || branchName

  const treatments = included.map((b, i) => ({
    no: String(i + 1),
    treatment_list:    itemName(b),
    treatment_listEN:  itemNameEN(b),
    treatment_price:   formatBaht2(normalLine(b)),
    treatment_priceEN: formatBaht2(normalLine(b)),
  }))
  const total = included.reduce((acc, b) => acc + normalLine(b), 0)

  return {
    fullname:  baseData.patient_name || '',
    HN:        baseData.hn || '',
    address:   lang === 'en' ? pickAddressEN(patient) : pickAddress(patient),
    doctor_fullname: pickDoctorFullname(doctor, treatment) || baseData.doctor_name || '',
    doctor_license:  pickDoctorLicense(treatment, doctor),
    company_name:      company.name || company.company_name || '',
    company_taxnumber: String(company.tax_number ?? company.taxnumber ?? company.tax_id ?? company.taxid ?? company.tax_no ?? '').trim(),
    company_address:   String(company.address ?? company.addr ?? company.address1 ?? company.full_address ?? company.location ?? '').trim(),
    branch_name:       branchName,
    branch_license_number: String(branch?.license_number ?? branch?.branch_license_number ?? branch?.license ?? branch?.license_no ?? '').trim(),
    branch_ssonumber:      String(branch?.sso_number ?? branch?.ssonumber ?? branch?.sso ?? branch?.sso_no ?? branch?.sso_number_text ?? '').trim(),
    ref_num:   buildRefNum(treatment),
    employee_fullname: pickEmployeeName(employee) || (me?.name || me?.email || '').trim(),
    current_date: lang === 'en' ? engShortDate(new Date()) : thaiShortDate(new Date()),
    treatments,
    treatment_price_total: formatBaht2(total),
    price_total:           formatBaht2(total),
    treatment_price_totalThai: bahtText(total),
    price_totalThai:           bahtText(total),
    payment_method: paymentMethod,
    service_date:   treatment?.date ? thaiShortDate(new Date(treatment.date)) : '',
    service_dateEN: treatment?.date ? engShortDate(new Date(treatment.date)) : '',
    ...billingTrace(included, treatment, new Date()),

    // English variants
    fullnameEN:          pickPatientNameEN(patient),
    branch_nameEN:       branchNameEN,
    doctor_fullnameEN:   pickDoctorNameEN(doctor, treatment),
    doctor_fullnameEn:   pickDoctorNameEN(doctor, treatment),
    company_nameEN:      companyNameEN(company),
    company_addressEN:   companyAddressEN(company),
    employee_fullnameEN: pickEmployeeNameEN(employee) || (me?.name || me?.email || '').trim(),
    current_dateEN:      engShortDate(new Date()),
    treatment_price_totalEN: bahtTextEN(total),
    price_totalEN:           bahtTextEN(total),
  }
}

// Chief complaint from the chart — chart.history.cc, falling back to the first
// hx[] entry's cc (same source the Clinical card reads).
function chartChiefComplaint(chart) {
  if (!chart) return ''
  const h = chart.history
  if (h && typeof h === 'object' && h.cc != null && String(h.cc).trim()) return String(h.cc).trim()
  const hx = Array.isArray(chart.hx) ? chart.hx : []
  for (const e of hx) {
    if (e && e.cc != null && String(e.cc).trim()) return String(e.cc).trim()
  }
  return ''
}

// Medical certificate (ใบรับรองแพทย์). Not billing-driven — diagnosis/treatment/
// opinion come from the dialog; everything else is DB-derived (patient/doctor/
// branch). `treatmentText` is the free-text "Treatment Provided" (distinct from
// the treatment record object).
function buildMedicalCertPayload({ baseData, treatment, patient, me, lang = 'th', diagnosis, treatmentText, opinion, branch, branchMap, branchMapEN, doctor, employee }) {
  const branchName = (branch && (branch.name || branch.branch_name))
    || (branchMap && branchMap[String(treatment?.branch_id ?? treatment?.branch?.id)]) || ''
  const branchNameEN = (branch && (branch.nameEng || branch.name_eng || branch.name_en || branch.nameEN))
    || (branchMapEN && branchMapEN[String(treatment?.branch_id ?? treatment?.branch?.id)]) || branchName
  const now = new Date()
  const docObj = doctor || treatment?.doctor || {}
  const treatDate = treatment?.date
  const doctorFullname = pickDoctorFullname(doctor, treatment) || baseData.doctor_name || ''
  return {
    fullname: baseData.patient_name || '',
    HN: baseData.hn || '',
    doctor_fullname: doctorFullname,
    doctor_prefix: doctorPrefixToken(docObj, doctorFullname),
    doctor_license: pickDoctorLicense(treatment, doctor),
    branch_name: branchName,
    branch_license_number: String(branch?.license_number ?? branch?.branch_license_number ?? branch?.license ?? branch?.license_no ?? '').trim(),
    branch_ssonumber: String(branch?.sso_number ?? branch?.ssonumber ?? branch?.sso ?? branch?.sso_no ?? '').trim(),
    patient_prefix: String(patient?.prefix ?? patient?.title ?? '').trim(),
    patient_fullname: [patient?.name, patient?.surname].filter(Boolean).join(' ').trim() || patient?.fullname || baseData.patient_name || '',
    patient_age: computePatientAge(patient),
    age: computePatientAge(patient),
    // Buddhist-era year like every other Thai document (formatThaiDate is the
    // UI formatter and prints the Gregorian year: "4 มีนาคม 2026").
    date_thai: treatDate ? thaiLongDateBE(treatDate) : '',
    date: treatDate ? engShortDate(new Date(treatDate)) : '',
    diagnosis: diagnosis || '',
    treatment: treatmentText || '',
    opinion: opinion || '',
    current_date: lang === 'en' ? engShortDate(now) : thaiShortDate(now),
    current_time: hhmm(now.getTime()),
    ref_num: buildRefNum(treatment),
    // Footer trace line of the HTML template (see buildReceiptPayload).
    treatment_ids: String(treatment?.id ?? treatment?.treatment_id ?? ''),
    printed_at:   `${thaiShortDate(now)} ${bkkTime(now)} น.`,
    printed_atEN: `${engShortDate(now)} ${bkkTime(now)}`,

    // English variants
    fullnameEN: pickPatientNameEN(patient),
    fullnameEn: pickPatientNameEN(patient), // template uses this casing in the body
    doctor_fullnameEN: pickDoctorNameEN(doctor, treatment),
    branch_nameEN: branchNameEN,
  }
}

// Referral note (ใบส่งตัว) — same DB-derived header as the medical certificate;
// the single input `refer_detail` is rich text (HTML from the editor) rendered
// as formatted blocks by the Apps Script.
// Combine a preset list (array) with the free-text "_other" field (a
// comma-separated string) into one comma-joined string — e.g.
// allergy ["เพนนิซิลิน"] + allergy_other "ซัลฟา, ไอบูโพรเฟน"
// → "เพนนิซิลิน, ซัลฟา, ไอบูโพรเฟน". Empty/blank entries are dropped.
function combineMedicalList(list, other) {
  const items = [
    ...(Array.isArray(list) ? list : (list ? [list] : [])),
    ...(other ? String(other).split(/,\s*/) : []),
  ].map((s) => String(s).trim()).filter(Boolean)
  return items.join(', ')
}

function buildReferralPayload({ baseData, treatment, patient, me, lang = 'th', referDetail, branch, branchMap, branchMapEN, doctor, employee }) {
  const branchName = (branch && (branch.name || branch.branch_name))
    || (branchMap && branchMap[String(treatment?.branch_id ?? treatment?.branch?.id)]) || ''
  const branchNameEN = (branch && (branch.nameEng || branch.name_eng || branch.name_en || branch.nameEN))
    || (branchMapEN && branchMapEN[String(treatment?.branch_id ?? treatment?.branch?.id)]) || branchName
  const now = new Date()
  const docObj = doctor || treatment?.doctor || {}
  const treatDate = treatment?.date
  const doctorFullname = pickDoctorFullname(doctor, treatment) || baseData.doctor_name || ''
  return {
    fullname: baseData.patient_name || '',
    HN: baseData.hn || '',
    doctor_fullname: doctorFullname,
    doctor_prefix: doctorPrefixToken(docObj, doctorFullname),
    doctor_license: pickDoctorLicense(treatment, doctor),
    branch_name: branchName,
    branch_license_number: String(branch?.license_number ?? branch?.branch_license_number ?? branch?.license ?? branch?.license_no ?? '').trim(),
    branch_ssonumber: String(branch?.sso_number ?? branch?.ssonumber ?? branch?.sso ?? branch?.sso_no ?? '').trim(),
    patient_prefix: String(patient?.prefix ?? patient?.title ?? '').trim(),
    paitent_prefix: String(patient?.prefix ?? patient?.title ?? '').trim(), // xray template's misspelled token
    patient_fullname: [patient?.name, patient?.surname].filter(Boolean).join(' ').trim() || patient?.fullname || baseData.patient_name || '',
    patient_age: computePatientAge(patient),
    age: computePatientAge(patient),
    // Tooth Taxi checkup form — preset list + free-text "_other", comma-joined.
    patient_allergy: combineMedicalList(patient?.allergy, patient?.allergy_other),
    patient_disease: combineMedicalList(patient?.disease, patient?.disease_other),
    date_thai: treatDate ? thaiLongDateBE(treatDate) : '',
    date_treatment: treatDate ? thaiShortDate(new Date(treatDate)) : '', // 01 มิ.ย. 2569
    date: treatDate ? engShortDate(new Date(treatDate)) : '',
    refer_detail: referDetail || '',
    ref_num: buildRefNum(treatment),
    // Footer trace line of the HTML templates (see buildReceiptPayload).
    treatment_ids: String(treatment?.id ?? treatment?.treatment_id ?? ''),
    printed_at:   `${thaiShortDate(now)} ${bkkTime(now)} น.`,
    printed_atEN: `${engShortDate(now)} ${bkkTime(now)}`,
    current_date: lang === 'en' ? engShortDate(now) : thaiShortDate(now),
    current_time: hhmm(now.getTime()),

    // English variants
    fullnameEN: pickPatientNameEN(patient),
    fullnameEn: pickPatientNameEN(patient),
    doctor_fullnameEN: pickDoctorNameEN(doctor, treatment),
    branch_nameEN: branchNameEN,
  }
}

/* -------------------------------------------------------------------------- */
/* Thai baht text (อ่านจำนวนเงินเป็นตัวอักษร)                                    */
/* -------------------------------------------------------------------------- */

function bahtText(amount) {
  const n = num(amount)
  if (n === 0) return 'ศูนย์บาทถ้วน'
  const neg = n < 0
  const abs = Math.abs(n)
  const baht = Math.floor(abs)
  const satang = Math.round((abs - baht) * 100)
  let out = ''
  if (baht > 0) out += readThaiInt(baht) + 'บาท'
  if (satang > 0) out += readThaiInt(satang) + 'สตางค์'
  else out += baht > 0 ? 'ถ้วน' : ''
  if (baht === 0 && satang > 0) out = readThaiInt(satang) + 'สตางค์'
  return (neg ? 'ลบ' : '') + out
}

function readThaiInt(num0) {
  const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า']
  const places = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน']
  let n = Math.floor(Math.abs(num0))
  if (n === 0) return 'ศูนย์'
  // Handle millions recursively.
  if (n >= 1000000) {
    return readThaiInt(Math.floor(n / 1000000)) + 'ล้าน' + (n % 1000000 ? readThaiInt(n % 1000000) : '')
  }
  let s = ''
  const str = String(n)
  const len = str.length
  for (let i = 0; i < len; i++) {
    const d = Number(str[i])
    const place = len - i - 1
    if (d === 0) continue
    if (place === 0 && d === 1 && len > 1) { s += 'เอ็ด'; continue }
    if (place === 1 && d === 2) { s += 'ยี่' + places[place]; continue }
    if (place === 1 && d === 1) { s += places[place]; continue }
    s += digits[d] + places[place]
  }
  return s
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Patient age computed from date of birth (records store the birthday, not a
// precomputed age). Returns "-" when there's no usable birthday.
function computePatientAge(p) {
  const dob = p && (p.dob ?? p.birth_date ?? p.birthday ?? p.birthdate ?? p.date_of_birth ?? p.dateOfBirth)
  if (dob == null || String(dob).trim() === '') return '-'
  let d
  if (typeof dob === 'number') d = new Date(dob)
  else if (/^\d{10,}$/.test(String(dob).trim())) d = new Date(Number(dob)) // unix ms
  else d = new Date(dob)
  if (isNaN(d.getTime())) return '-'
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--
  if (age < 0 || age > 150) return '-'
  return String(age)
}

function buildBaseData(patient, hn, treatment) {
  const p = patient || {}
  const fullName = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || ''
  const doctor = treatment?.doctor || {}
  const docName = (doctor.code || doctor.name || doctor.full_name || '').trim()
  const age = computePatientAge(p)
  return {
    patient_name:     fullName,
    hn:               hn || p.hn || p.HN || '',
    patient_age:      age,
    age,
    phone:            p.phone || p.tel || '',
    treatment_date:   treatment?.date || '',
    // BE, like every printed date: this is the {{treatment_date_th}} base placeholder
    // sent to every non-receipt template (handleGenerate), and formatThaiDate alone
    // prints the CE year.
    treatment_date_th: treatment?.date ? thaiLongDateBE(treatment.date) : '',
    treatment_detail: treatment?.detail || treatment?.details || '',
    doctor_name:      docName,
  }
}
